"use client";

import { DetailSkeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  MARKDOWN_DOCUMENT_PREVIEW_CLASS_NAME,
  MarkdownPreviewContent,
  MarkdownPreviewToggle,
  MarkdownPreviewToolbar,
  type MarkdownPreviewMode,
} from "@/components/markdown-preview";
import { PageHeader } from "@/components/page-header";
import { useTabTitle } from "@/components/tab-title-provider";
import { PolicyButton } from "@/components/policy-button";
import { SearchDialog, type SearchDialogGroup } from "@/components/search-dialog";
import { AudioLinesIcon } from "@/components/ui/audio-lines";
import { GalleryVerticalEndIcon } from "@/components/ui/gallery-vertical-end";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageResponse } from "@/components/ai-elements/message";
import {
  Artifact,
  ArtifactContent,
} from "@/components/ai-elements/artifact";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DownloadIcon as AnimatedDownloadIcon } from "@/components/ui/download";
import { DownvoteIcon } from "@/components/ui/downvote";
import { CopyIcon } from "@/components/ui/copy";
import { DeleteIcon } from "@/components/ui/delete";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { MailCheckIcon } from "@/components/ui/mail-check";
import { PlayIcon } from "@/components/ui/play";
import { RefreshCWIcon } from "@/components/ui/refresh-cw";
import { SearchIcon } from "@/components/ui/search";
import { SendIcon } from "@/components/ui/send";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { BorderBeam } from "@/components/ui/border-beam";
import { SaveButton } from "@/components/ui/save-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { TimerIcon } from "@/components/ui/timer";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { TextAnimate } from "@/registry/magicui/text-animate";
import { TypingAnimation } from "@/registry/magicui/typing-animation";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { PlusIcon as AnimatedPlusIcon } from "@/components/ui/plus";
import { UpvoteIcon } from "@/components/ui/upvote";
import { UsersIcon } from "@/components/ui/users";
import { XIcon as AnimatedXIcon } from "@/components/ui/x";
import { useMeetingRecordings } from "@/hooks/use-meeting-recordings";
import { useProjectMeetingRecordings } from "@/hooks/use-project-meeting-recordings";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { getMeetingTabStatus } from "@/lib/tab-title";
import { mergeMeetingAttendees } from "@/lib/meeting-attendees";
import {
  createMeetingDraftSnapshot,
  isMeetingDraftSnapshotEqual,
  type MeetingDraftSnapshot,
} from "@/lib/meeting-draft-snapshot";
import { PREVIOUS_NOTES_SUPPORTED_EXTENSIONS } from "@/lib/previous-meeting-notes";
import type {
  DbLayout,
  DbMeetingAttendee,
  DbMeetingRecording,
  DbMeetingStatus,
  DbUserSearchUser,
} from "@/types/db";
import { AlertCircleIcon, CheckCircle2Icon, FileUp, InfoIcon, Maximize2, MicIcon, MicOffIcon, PanelRight, PanelRightClose, PencilLine, Redo2, Square, Undo2 } from "lucide-react";
import { useInView } from "motion/react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

type MeetingMinutesPanelProps = {
  onBack?: () => void;
  recordingId?: string | null;
  projectRecordingId?: string | null;
  anotherRecordingActive?: boolean;
  isVisible?: boolean;
  onRecordingStateChange?: (isRecording: boolean, isMuted: boolean) => void;
};

type LeftColumnAutosaveStatus = "idle" | "saving" | "saved" | "error";
type ManualSaveStatus = "idle" | "saved";

type TranscribeChunkResponse = {
  text?: string;
  error?: string;
};

const MIN_TRANSCRIBABLE_CHUNK_BYTES = 2 * 1024;
const CHUNK_TIMESLICE_MS = 30_000;
const FAILURE_EMAIL_THRESHOLDS = new Set([4, 20, 80, 120]);
const MEETING_TITLE_TYPING_SPEED = 20;
const TITLE_ANIMATION_MIN_DURATION = 350;
const TITLE_ANIMATION_BUFFER = 120;
const DEFAULT_MEETING_LAYOUT_NAME = "General";
function normalizeMimeType(mimeType?: string) {
  if (!mimeType) {
    return "";
  }

  return mimeType.split(";")[0].trim().toLowerCase();
}

function formatDuration(totalSeconds: number) {
  const safeSeconds = Number.isFinite(totalSeconds) ? Math.max(0, totalSeconds) : 0;
  const totalMinutes = Math.floor(safeSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
}

function formatChunkDuration(totalSeconds: number) {
  const safeSeconds = Number.isFinite(totalSeconds) ? Math.max(0, totalSeconds) : 0;

  if (safeSeconds < 60) {
    return `${safeSeconds}s`;
  }

  const totalMinutes = Math.floor(safeSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
}

function getAudioFileExtension(mimeType: string): string {
  const normalized = normalizeMimeType(mimeType);

  if (normalized === "audio/webm") {
    return "webm";
  }

  if (normalized === "audio/ogg" || normalized === "audio/oga") {
    return "ogg";
  }

  if (normalized === "audio/mp4") {
    return "mp4";
  }

  if (
    normalized === "audio/mpeg" ||
    normalized === "audio/mp3" ||
    normalized === "audio/mpga"
  ) {
    return "mp3";
  }

  if (normalized === "audio/wav") {
    return "wav";
  }

  if (normalized === "audio/flac") {
    return "flac";
  }

  if (normalized === "audio/x-m4a" || normalized === "audio/m4a") {
    return "m4a";
  }

  return "webm";
}

function getSupportedRecorderMimeType() {
  if (typeof window === "undefined" || typeof MediaRecorder === "undefined") {
    return undefined;
  }

  const options = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"];

  return options.find((option) => MediaRecorder.isTypeSupported(option));
}

function normalizeAttendees(value: unknown): DbMeetingAttendee[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => {
      if (!entry || typeof entry !== "object") {
        return null;
      }

      const rawName = "name" in entry ? entry.name : null;
      if (typeof rawName !== "string" || !rawName.trim()) {
        return null;
      }

      const rawUserId = "user_id" in entry ? entry.user_id : null;
      return {
        name: rawName.trim(),
        user_id: typeof rawUserId === "string" && rawUserId.trim() ? rawUserId : null,
      };
    })
    .filter((entry): entry is DbMeetingAttendee => entry !== null);
}

type MeetingStatusPresentation = {
  label: string;
  mobileLabel: string;
  toneClassName: string;
  icon:
    | "idle"
    | "recording"
    | "ready"
    | "processing"
    | "email"
    | "complete"
    | "failed";
};

function findMentionQuery(value: string): string | null {
  // The query after "@" may contain spaces so full names ("@John Smith")
  // stay searchable; it only ends at another "@" or the end of the input.
  const match = value.match(/(?:^|\s)@([^@]*)$/);
  return match ? match[1] : null;
}

function toUserSearchLabel(user: DbUserSearchUser): string {
  return user.display_name?.trim() || user.username?.trim() || user.email?.trim() || "Unknown user";
}

function formatMeetingDate(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
  }).format(new Date(value));
}

function SaveIndicator({ status }: { status: LeftColumnAutosaveStatus }) {
  if (status === "idle") return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="inline-flex size-5 items-center justify-center">
          {status === "saving" ? (
            <Spinner className="size-3.5" />
          ) : status === "error" ? (
            <AlertCircleIcon className="size-3.5 text-destructive" />
          ) : (
            <CheckCircle2Icon className="size-3.5 text-activated animate-in zoom-in-95 duration-200" />
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="right" className="text-xs">
        <p>
          {status === "saving"
            ? "Saving…"
            : status === "saved"
              ? "Saved"
              : "Save failed"}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

function WaveformBars({ analyser, barClassName = "bg-activated" }: { analyser: AnalyserNode | null; barClassName?: string }) {
  const barsRef = useRef<(HTMLDivElement | null)[]>([]);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!analyser) return;

    const BAR_COUNT = 5;
    const node = analyser;
    const bufferLength = node.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    function draw() {
      rafRef.current = requestAnimationFrame(draw);
      node.getByteFrequencyData(dataArray);
      for (let i = 0; i < BAR_COUNT; i++) {
        const start = Math.floor((i / BAR_COUNT) * bufferLength * 0.6);
        const end = Math.max(start + 1, Math.floor(((i + 1) / BAR_COUNT) * bufferLength * 0.6));
        let sum = 0;
        for (let j = start; j < end; j++) sum += dataArray[j];
        const avg = sum / (end - start);
        const bar = barsRef.current[i];
        if (bar) bar.style.transform = `scaleY(${Math.max(0.1, avg / 255)})`;
      }
    }

    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [analyser]);

  if (!analyser) return null;

  return (
    <div className="inline-flex h-9 items-center gap-px px-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          ref={(el) => {
            barsRef.current[i] = el;
          }}
          className={cn("w-1 rounded-full", barClassName)}
          style={{ height: "70%", transform: "scaleY(0.1)", transformOrigin: "center" }}
        />
      ))}
    </div>
  );
}

export function MeetingMinutesPanel({ onBack, recordingId, projectRecordingId, anotherRecordingActive = false, isVisible = true, onRecordingStateChange }: MeetingMinutesPanelProps) {
  const router = useRouter();
  const isProjectRecording = !!projectRecordingId;
  const activeId = projectRecordingId ?? recordingId;
  const { meetings } = useMeetingRecordings();

  const [meeting, setMeeting] = useState<DbMeetingRecording | null>(null);
  const meetingProjectId = isProjectRecording
    ? (meeting as { project_id?: string } | null)?.project_id ?? null
    : null;
  const { recordings: siblingProjectMeetings } = useProjectMeetingRecordings(meetingProjectId);
  const [projectTitleContext, setProjectTitleContext] = useState<{
    name: string;
    code: string;
  } | null>(null);
  const [minutesDraft, setMinutesDraft] = useState("");
  // True only when the draft was changed by typing in the markdown editor —
  // gates the autosave effect so programmatic draft updates never persist.
  const minutesEditDirtyRef = useRef(false);
  const [meetingContextDraft, setMeetingContextDraft] = useState("");
  const [meetingInstructionsDraft, setMeetingInstructionsDraft] = useState("");
  const [attendeesDraft, setAttendeesDraft] = useState<DbMeetingAttendee[]>([]);
  const [previousMeetingIdDraft, setPreviousMeetingIdDraft] = useState<string | null>(null);
  const [previousMeetingNotesDraft, setPreviousMeetingNotesDraft] = useState("");
  const [previousNotesUploadStatus, setPreviousNotesUploadStatus] = useState<"idle" | "uploading" | "done" | "error">("idle");
  const [previousNotesFileName, setPreviousNotesFileName] = useState<string | null>(null);
  const previousNotesFileInputRef = useRef<HTMLInputElement>(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [attendeeInput, setAttendeeInput] = useState("");
  const [userSearchResults, setUserSearchResults] = useState<DbUserSearchUser[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [feedback, setFeedback] = useState<"positive" | "negative" | null>(null);
  const [minutesView, setMinutesView] = useState<MarkdownPreviewMode>("preview");
  const [minutesEditorOpen, setMinutesEditorOpen] = useState(false);
  const [minutesEditorMode, setMinutesEditorMode] = useState<MarkdownPreviewMode>("split");
  const [minutesPanelWidth, setMinutesPanelWidth] = useState<number | null>(null);
  const [minutesPanelOpen, setMinutesPanelOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaybackRunning, setIsPlaybackRunning] = useState(false);
  const [isRecordingPaused, setIsRecordingPaused] = useState(false);
  const [latestTranscribedText, setLatestTranscribedText] = useState("");
  const [liveTranscript, setLiveTranscript] = useState("");
  const [pendingTranscriptionCount, setPendingTranscriptionCount] = useState(0);
  const [liveDurationSeconds, setLiveDurationSeconds] = useState(0);
  const [isGeneratingMinutes, setIsGeneratingMinutes] = useState(false);
  const [isStoppingRecording, setIsStoppingRecording] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isCreatingLinkedMeeting, setIsCreatingLinkedMeeting] = useState(false);
  const [manualSaveStatus, setManualSaveStatus] = useState<ManualSaveStatus>("idle");
  const [leftColumnAutosaveStatus, setLeftColumnAutosaveStatus] =
    useState<LeftColumnAutosaveStatus>("idle");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFeedbackUpdating, setIsFeedbackUpdating] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeletingMeeting, setIsDeletingMeeting] = useState(false);
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [isGeneratingAutoTitle, setIsGeneratingAutoTitle] = useState(false);
  const [isAutoTitleAnimating, setIsAutoTitleAnimating] = useState(false);
  const [associatedPreviousMeetingTitle, setAssociatedPreviousMeetingTitle] = useState<string | null>(null);
  const [animateAssociatedPreviousMeetingTitle, setAnimateAssociatedPreviousMeetingTitle] = useState(false);
  const [layouts, setLayouts] = useState<DbLayout[]>([]);
  const [isLoadingLayouts, setIsLoadingLayouts] = useState(false);
  const [layoutIdDraft, setLayoutIdDraft] = useState<string | null>(null);
  const [layoutPreviewOpen, setLayoutPreviewOpen] = useState(false);
  const [previewLayoutId, setPreviewLayoutId] = useState<string | null>(null);
  const [notesDocxPath, setNotesDocxPath] = useState<string | null>(null);
  const [isConsentDialogOpen, setIsConsentDialogOpen] = useState(false);
  const [includeCallAudio, setIncludeCallAudio] = useState(false);
  const [isNoChangesDialogOpen, setIsNoChangesDialogOpen] = useState(false);
  const [isSendDialogOpen, setIsSendDialogOpen] = useState(false);
  const [selectionEditUndo, setSelectionEditUndo] = useState<string | null>(null);
  const [selectionEditRedo, setSelectionEditRedo] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const displayStreamRef = useRef<MediaStream | null>(null);
  const segmentTimeoutRef = useRef<number | null>(null);
  const segmentStartedAtRef = useRef<number>(Date.now());
  const recordingActiveRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const [liveAnalyser, setLiveAnalyser] = useState<AnalyserNode | null>(null);
  const [liveCallAnalyser, setLiveCallAnalyser] = useState<AnalyserNode | null>(null);
  const queuedChunkChainRef = useRef<Promise<void>>(Promise.resolve());
  // Set once the row this panel is editing is gone from the DB (deleted here,
  // in another tab, or from the meetings list). Every draft save after that is
  // a no-op: retrying cannot bring the row back, and the save endpoint has no
  // way to tell a resurrect apart from a new draft.
  const draftRowMissingRef = useRef(false);
  const transcriptRef = useRef("");
  const transcribedChunksRef = useRef(0);
  const failedChunksRef = useRef(0);
  const consecutiveFailedChunksRef = useRef(0);
  const notifiedFailureThresholdsRef = useRef(new Set<number>());
  const [showChunkFailureBanner, setShowChunkFailureBanner] = useState(false);

  // Snapshot of the generation-input fields (context, attendees, previous
  // meeting notes/association, layout) as sent with the most recent draft
  // generation. `pending` is captured at kickoff and promoted to `last` only
  // when the generation succeeds; Retry compares against `last` to skip
  // regeneration when nothing feeding generation has changed.
  const lastDraftSnapshotRef = useRef<MeetingDraftSnapshot | null>(null);
  const pendingDraftSnapshotRef = useRef<MeetingDraftSnapshot | null>(null);

  const baseDurationSecondsRef = useRef(0);
  const recordingStartedAtRef = useRef<number | null>(null);
  const recorderMimeTypeRef = useRef("");
  const transcriptionErrorShownRef = useRef(false);
  const pendingTranscriptionCountRef = useRef(0);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const detailsCardRef = useRef<HTMLDivElement>(null);
  const minutesCardRef = useRef<HTMLDivElement>(null);
  const _minutesInView = useInView(minutesCardRef, { root: scrollContainerRef, amount: 0.1 });

  const meetingAttendeesJson = useMemo(
    () => JSON.stringify(normalizeAttendees(meeting?.attendees)),
    [meeting?.attendees]
  );
  const attendeesDraftJson = useMemo(() => JSON.stringify(attendeesDraft), [attendeesDraft]);
  const persistedMeetingContext = meeting?.meeting_context ?? "";
  const persistedMeetingInstructions = meeting?.meeting_instructions ?? "";
  const persistedPreviousMeetingNotes = meeting?.previous_meeting_notes ?? "";
  const availableLayouts = useMemo(
    () => layouts.filter((layout) => layout.is_active && (layout.type === "meeting_minutes" || layout.type === "general")),
    [layouts]
  );
  const defaultLayout = useMemo(
    () => availableLayouts.find((layout) => layout.name.trim().toLowerCase() === "general") ?? availableLayouts[0] ?? null,
    [availableLayouts]
  );
  const resolvedLayoutIdDraft = layoutIdDraft ?? defaultLayout?.id ?? null;
  const selectedLayout = useMemo(
    () => availableLayouts.find((layout) => layout.id === resolvedLayoutIdDraft) ?? defaultLayout,
    [availableLayouts, defaultLayout, resolvedLayoutIdDraft]
  );
  const previewingLayout = useMemo(
    () => availableLayouts.find((l) => l.id === (previewLayoutId ?? resolvedLayoutIdDraft)) ?? selectedLayout,
    [availableLayouts, previewLayoutId, resolvedLayoutIdDraft, selectedLayout]
  );
  const persistedLayoutId = meeting?.layout_id ?? null;
  const hasPersistedTranscript = Boolean(meeting?.transcript?.trim() || liveTranscript.trim());
  const isRecordingInterrupted = meeting?.status === "RECORDING" && !isPlaybackRunning && !isStoppingRecording;
  const isRecordingCompleted =
    meeting?.status === "RECORDING_COMPLETE" ||
    meeting?.status === "RECORDING_COMPLETED" ||
    (
      meeting?.status === "RECORDING_NOT_STARTED" &&
      hasPersistedTranscript &&
      !isPlaybackRunning &&
      !isStoppingRecording &&
      !meeting?.minutes?.trim()
    );
  const isProcessingNotes = meeting?.status === "PROCESSING_NOTES";
  const isProcessingDocx = meeting?.status === "PROCESSING_DOCX" || meeting?.status === "SENDING_EMAIL";
  const isProcessingInBackground = isProcessingNotes || isProcessingDocx;
  const isGenerationFailed = meeting?.status === "FAILED";
  const isGenerationHung =
    isProcessingInBackground &&
    Boolean(meeting?.updated_at) &&
    Date.now() - new Date(meeting!.updated_at!).getTime() > 15 * 60 * 1000;
  const isRecordingLocked = Boolean(meeting?.minutes?.trim()) || isProcessingInBackground || isRecordingCompleted;
  const isMeetingDetailsEditingDisabled =
    isGeneratingMinutes || isDeletingMeeting || isSendingEmail;
  const canCreateLinkedMeeting = Boolean(meeting?.minutes?.trim());
  const lastTranscriptLine = useMemo(() => {
    const explicitLastChunk = latestTranscribedText.trim();
    if (explicitLastChunk) {
      return explicitLastChunk;
    }

    const transcriptLines = liveTranscript
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    return transcriptLines[transcriptLines.length - 1] ?? "";
  }, [latestTranscribedText, liveTranscript]);
  const meetingStatusPresentation = useMemo<MeetingStatusPresentation>(() => {
    if (isGenerationHung) {
      return {
        label: "Generation stalled",
        mobileLabel: "Stalled",
        toneClassName: "border-destructive/40 bg-destructive/10 text-destructive",
        icon: "failed",
      };
    }

    switch (meeting?.status) {
      case "RECORDING":
        if (isRecordingInterrupted) {
          return {
            label: "Recording interrupted",
            mobileLabel: "Interrupted",
            toneClassName: "border-destructive/40 bg-destructive/10 text-destructive",
            icon: "failed",
          };
        }
        return {
          label: "Recording",
          mobileLabel: "Recording",
          toneClassName: "border-primary/30 bg-primary/10 text-primary",
          icon: "recording",
        };
      case "RECORDING_COMPLETE":
      case "RECORDING_COMPLETED":
        return {
          label: "Ready to generate",
          mobileLabel: "Ready",
          toneClassName: "border-border bg-muted text-foreground",
          icon: "ready",
        };
      case "RECORDING_NOT_STARTED":
        if (hasPersistedTranscript && !isPlaybackRunning && !meeting?.minutes?.trim()) {
          return {
            label: "Ready to generate",
            mobileLabel: "Ready",
            toneClassName: "border-border bg-muted text-foreground",
            icon: "ready",
          };
        }
        return {
          label: "Not started",
          mobileLabel: "Idle",
          toneClassName: "border-border bg-background text-muted-foreground",
          icon: "idle",
        };
      case "PROCESSING_NOTES":
        return {
          label: "Generating minutes",
          mobileLabel: "Minutes",
          toneClassName: "border-border bg-muted text-muted-foreground",
          icon: "processing",
        };
      case "PROCESSING_DOCX":
        return {
          label: "Creating DOCX",
          mobileLabel: "DOCX",
          toneClassName: "border-border bg-muted text-muted-foreground",
          icon: "processing",
        };
      case "SENDING_EMAIL":
        return {
          label: "Sending email",
          mobileLabel: "Email",
          toneClassName: "border-border bg-muted text-muted-foreground",
          icon: "email",
        };
      case "PROCESSING_COMPLETE":
        return {
          label: "Completed",
          mobileLabel: "Done",
          toneClassName: "border-border bg-muted text-foreground",
          icon: "complete",
        };
      case "FAILED":
        return {
          label: "Failed",
          mobileLabel: "Failed",
          toneClassName: "border-destructive/40 bg-destructive/10 text-destructive",
          icon: "failed",
        };
      default:
        return {
          label: "Not started",
          mobileLabel: "Idle",
          toneClassName: "border-border bg-background text-muted-foreground",
          icon: "idle",
        };
    }
  }, [hasPersistedTranscript, isGenerationHung, isPlaybackRunning, isRecordingInterrupted, meeting?.minutes, meeting?.status]);

  useEffect(() => {
    onRecordingStateChange?.(isPlaybackRunning, isRecordingPaused);
  }, [isPlaybackRunning, isRecordingPaused, onRecordingStateChange]);

  // The tab status follows the live recorder, not the persisted row: a
  // recording abandoned by a closed tab leaves status = RECORDING behind, and
  // the tab must not keep claiming to record when no audio is being captured.
  const meetingTabStatus = useMemo(() => {
    if (isPlaybackRunning) {
      return isRecordingPaused ? "Muted" : "Recording";
    }
    const persistedStatus = getMeetingTabStatus(meetingStatusPresentation.label);
    return persistedStatus === "Recording" ? null : persistedStatus;
  }, [isPlaybackRunning, isRecordingPaused, meetingStatusPresentation.label]);
  useTabTitle(
    meeting && isVisible ? meeting.title?.trim() || "Untitled meeting" : null,
    meeting && isVisible ? meetingTabStatus : null
  );

  const renderMeetingStatusIcon = useCallback((sizeClassName = "size-3.5") => {
    switch (meetingStatusPresentation.icon) {
      case "recording":
        return <MicIcon className={cn(sizeClassName, "text-current")} />;
      case "ready":
      case "complete":
        return <CheckCircle2Icon className={cn(sizeClassName, "text-current")} />;
      case "processing":
        return <PencilLine className={cn(sizeClassName, "text-current")} />;
      case "email":
        return <MailCheckIcon className={cn(sizeClassName, "text-current")} />;
      case "failed":
        return <AlertCircleIcon className={cn(sizeClassName, "text-current")} />;
      case "idle":
      default:
        return <MicOffIcon className={cn(sizeClassName, "text-current")} />;
    }
  }, [meetingStatusPresentation.icon]);

  useEffect(() => {
    let cancelled = false;

    const loadLayouts = async () => {
      setIsLoadingLayouts(true);

      const { data, error: layoutsError } = await supabase
        .from("layouts")
        .select("id,created_by,name,description,type,structure,is_active,config,created_at,modified_at")
        .eq("is_active", true)
        .in("type", ["meeting_minutes", "general"])
        .order("name", { ascending: true });

      if (cancelled) {
        return;
      }

      if (layoutsError) {
        console.error("[MeetingMinutesPanel] load layouts:", layoutsError);
        setLayouts([]);
        setIsLoadingLayouts(false);
        return;
      }

      setLayouts((data as DbLayout[] | null) ?? []);
      setIsLoadingLayouts(false);
    };

    void loadLayouts();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!activeId) {
      setMeeting(null);
      setProjectTitleContext(null);
      setMinutesDraft("");
      setMeetingContextDraft("");
      setMeetingInstructionsDraft("");
      setAttendeesDraft([]);
      setPreviousMeetingIdDraft(null);
      setPreviousMeetingNotesDraft("");
      setPreviousNotesUploadStatus("idle");
      setPreviousNotesFileName(null);
      setIsImportDialogOpen(false);
      setAttendeeInput("");
      setUserSearchResults([]);
      setManualSaveStatus("idle");
      setFeedback(null);
      setLeftColumnAutosaveStatus("idle");
      setLoading(false);
      setError(null);
      setIsPlaybackRunning(false);
      setIsRecordingPaused(false);
      setIsStoppingRecording(false);
      setLatestTranscribedText("");
      setLiveTranscript("");
      setPendingTranscriptionCount(0);
      pendingTranscriptionCountRef.current = 0;
      setLiveDurationSeconds(0);
      setIsGeneratingMinutes(false);
      setIsSendingEmail(false);
      setIsDeleteDialogOpen(false);
      setIsDeletingMeeting(false);
      setIsTitleEditing(false);
      setIsSavingTitle(false);
      setTitleDraft("");
      setIsGeneratingAutoTitle(false);
      setIsAutoTitleAnimating(false);
      setAssociatedPreviousMeetingTitle(null);
      setAnimateAssociatedPreviousMeetingTitle(false);
      setLayoutIdDraft(null);
      setNotesDocxPath(null);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      recordingActiveRef.current = false;
      if (segmentTimeoutRef.current) {
        window.clearTimeout(segmentTimeoutRef.current);
        segmentTimeoutRef.current = null;
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (displayStreamRef.current) {
        displayStreamRef.current.getTracks().forEach((track) => track.stop());
        displayStreamRef.current = null;
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
        micStreamRef.current = null;
      }
      mediaRecorderRef.current = null;
      mediaStreamRef.current = null;
      queuedChunkChainRef.current = Promise.resolve();
      transcriptRef.current = "";
      transcribedChunksRef.current = 0;
      failedChunksRef.current = 0;
      consecutiveFailedChunksRef.current = 0;
      notifiedFailureThresholdsRef.current.clear();
      transcriptionErrorShownRef.current = false;
      setShowChunkFailureBanner(false);
      baseDurationSecondsRef.current = 0;
      recordingStartedAtRef.current = null;
      recorderMimeTypeRef.current = "";
      draftRowMissingRef.current = false;
      lastDraftSnapshotRef.current = null;
      pendingDraftSnapshotRef.current = null;
      setIsNoChangesDialogOpen(false);
      setSelectionEditUndo(null);
      setSelectionEditRedo(null);
      return;
    }

    let cancelled = false;

    const loadMeeting = async () => {
      setLoading(true);
      setError(null);

      // Both tables carry the same columns since migration 20260902000001,
      // so one select serves both — project rows just add project_id and
      // is_confidential.
      const sharedColumns = `
        id,
        user_id,
        team_id,
        layout_id,
        previous_meeting_id,
        title,
        title_manually_edited,
        meeting_context,
        meeting_instructions,
        previous_meeting_notes,
        transcript,
        minutes,
        status,
        attendees,
        duration_seconds,
        transcribed_chunks,
        failed_chunks,
        email_sent,
        email_sent_at,
        feedback,
        notes_path,
        created_at,
        updated_at
      `;

      const { data, error: loadError } = await supabase
        .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
        .select(isProjectRecording ? `${sharedColumns}, project_id, is_confidential` : sharedColumns)
        .eq("id", activeId)
        .maybeSingle();

      if (cancelled) {
        return;
      }

      if (loadError) {
        console.error("[MeetingMinutesPanel] load meeting:", loadError);
        setMeeting(null);
        setProjectTitleContext(null);
        setFeedback(null);
        setError("Failed to load meeting notes.");
        setLoading(false);
        return;
      }

      if (!data) {
        setMeeting(null);
        setProjectTitleContext(null);
        setFeedback(null);
        setError("Meeting not found or you do not have access.");
        setLoading(false);
        return;
      }

      // No per-table translation: the two tables are structurally identical,
      // so a project row IS a DbMeetingRecording.
      const nextMeeting = data as unknown as DbMeetingRecording;
      const projectId = (data as unknown as { project_id?: string }).project_id;
      let project: { name?: string | null; project_number?: string | null } | null = null;

      if (isProjectRecording && projectId) {
        const { data: projectData, error: projectError } = await supabase
          .from("projects")
          .select("name, project_number")
          .eq("id", projectId)
          .maybeSingle();

        if (cancelled) return;

        if (projectError) {
          console.error("[MeetingMinutesPanel] load project title context:", projectError);
        } else {
          project = projectData;
        }
      }

      const nextAttendees = normalizeAttendees(nextMeeting.attendees);
      setMeeting({
        ...nextMeeting,
        attendees: nextAttendees,
      });
      setProjectTitleContext(
        isProjectRecording && project
          ? {
              name: project.name?.trim() ?? "",
              code: project.project_number?.trim() ?? "",
            }
          : null
      );
      draftRowMissingRef.current = false;
      transcriptRef.current = nextMeeting.transcript ?? "";
      transcribedChunksRef.current = Math.max(0, Number(nextMeeting.transcribed_chunks) || 0);
      failedChunksRef.current = Math.max(0, Number(nextMeeting.failed_chunks) || 0);
      baseDurationSecondsRef.current = Math.max(0, Number(nextMeeting.duration_seconds) || 0);
      recordingStartedAtRef.current = null;
      setLiveTranscript(nextMeeting.transcript ?? "");
      setLatestTranscribedText("");
      setPendingTranscriptionCount(0);
      pendingTranscriptionCountRef.current = 0;
      setLiveDurationSeconds(baseDurationSecondsRef.current);
      setIsPlaybackRunning(false);
      setIsRecordingPaused(false);
      setMinutesDraft(nextMeeting.minutes ?? "");
      setMeetingContextDraft(nextMeeting.meeting_context ?? "");
      setMeetingInstructionsDraft(nextMeeting.meeting_instructions ?? "");
      setAttendeesDraft(nextAttendees);
      setPreviousMeetingIdDraft(nextMeeting.previous_meeting_id ?? null);
      setLayoutIdDraft(nextMeeting.layout_id ?? null);
      setPreviousMeetingNotesDraft(nextMeeting.previous_meeting_notes ?? "");
      setPreviousNotesUploadStatus(nextMeeting.previous_meeting_notes ? "done" : "idle");
      setPreviousNotesFileName(null);
      setIsImportDialogOpen(false);
      setAttendeeInput("");
      setUserSearchResults([]);
      setManualSaveStatus("idle");
      setLeftColumnAutosaveStatus("idle");
      setIsGeneratingMinutes(false);
      setIsSendingEmail(false);
      setIsDeleteDialogOpen(false);
      setIsDeletingMeeting(false);
      setIsTitleEditing(false);
      setIsSavingTitle(false);
      setTitleDraft(nextMeeting.title ?? "");
      setIsGeneratingAutoTitle(false);
      setIsAutoTitleAnimating(false);
      setAssociatedPreviousMeetingTitle(null);
      setAnimateAssociatedPreviousMeetingTitle(false);
      setFeedback(
        nextMeeting.feedback === "positive" || nextMeeting.feedback === "negative"
          ? nextMeeting.feedback
          : null
      );
      setNotesDocxPath(nextMeeting.notes_path ?? null);
      // Best-effort re-draft baseline for meetings that already have a draft:
      // the persisted values stand in for the inputs of the last successful
      // generation (autosave keeps them in sync while the page is open).
      lastDraftSnapshotRef.current = nextMeeting.minutes?.trim()
        ? createMeetingDraftSnapshot({
            meetingContext: nextMeeting.meeting_context ?? "",
            meetingInstructions: nextMeeting.meeting_instructions ?? "",
            attendees: nextAttendees,
            previousMeetingId: nextMeeting.previous_meeting_id ?? null,
            previousMeetingNotes: nextMeeting.previous_meeting_notes ?? "",
            layoutId: nextMeeting.layout_id ?? null,
          })
        : null;
      pendingDraftSnapshotRef.current = null;
      setIsNoChangesDialogOpen(false);
      setSelectionEditUndo(null);
      setSelectionEditRedo(null);
      setLoading(false);
    };

    void loadMeeting();

    return () => {
      cancelled = true;
    };
  }, [activeId, isProjectRecording]);

  useEffect(() => {
    if (!meeting) {
      setLeftColumnAutosaveStatus("idle");
      return;
    }

    const hasAttendeesDiff = attendeesDraftJson !== meetingAttendeesJson;
    const hasMeetingContextDiff = meetingContextDraft !== persistedMeetingContext;
    const hasMeetingInstructionsDiff = meetingInstructionsDraft !== persistedMeetingInstructions;
    const hasPreviousNotesDiff = previousMeetingNotesDraft !== persistedPreviousMeetingNotes;
    const hasPreviousMeetingDiff = previousMeetingIdDraft !== (meeting.previous_meeting_id ?? null);
    const hasLayoutDiff = resolvedLayoutIdDraft !== persistedLayoutId;

    if (
      !hasAttendeesDiff &&
      !hasMeetingContextDiff &&
      !hasMeetingInstructionsDiff &&
      !hasPreviousNotesDiff &&
      !hasPreviousMeetingDiff &&
      !hasLayoutDiff
    ) {
      return;
    }

    let cancelled = false;
    setLeftColumnAutosaveStatus("saving");

    const timeout = window.setTimeout(async () => {
      const updatePayload = {
        attendees: attendeesDraft,
        meeting_context: meetingContextDraft,
        meeting_instructions: meetingInstructionsDraft,
        previous_meeting_notes: previousMeetingNotesDraft,
        previous_meeting_id: previousMeetingIdDraft,
        layout_id: resolvedLayoutIdDraft,
        updated_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
        .update(updatePayload)
        .eq("id", meeting.id);

      if (cancelled) {
        return;
      }

      if (updateError) {
        console.error("[MeetingMinutesPanel] autosave attendees:", updateError);
        setLeftColumnAutosaveStatus("error");
        return;
      }

      setMeeting((current) =>
        current
          ? {
              ...current,
              attendees: attendeesDraft,
              meeting_context: meetingContextDraft,
              meeting_instructions: meetingInstructionsDraft,
              previous_meeting_notes: previousMeetingNotesDraft,
              previous_meeting_id: previousMeetingIdDraft,
              layout_id: resolvedLayoutIdDraft,
            }
          : current
      );
      setLeftColumnAutosaveStatus("saved");
    }, 650);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [
    attendeesDraft,
    attendeesDraftJson,
    isProjectRecording,
    meeting,
    meetingAttendeesJson,
    meetingContextDraft,
    meetingInstructionsDraft,
    persistedLayoutId,
    persistedPreviousMeetingNotes,
    previousMeetingNotesDraft,
    previousMeetingIdDraft,
    persistedMeetingContext,
    persistedMeetingInstructions,
    resolvedLayoutIdDraft,
  ]);

  // Poll for completion while the pipeline is running (notes → docx → email).
  // Each tick reads only cheap status columns; the large `minutes` TEXT (and
  // title) is fetched once, when the status transitions to a ready state.
  useEffect(() => {
    if (!meeting || !isProcessingInBackground) {
      return;
    }

    type PollData = { status?: string; updated_at?: string | null; email_sent?: boolean; email_sent_at?: string | null; notes_path?: string };
    type ResultData = { minutes: string | null; title: string | null };

    const fetchResult = async (): Promise<ResultData | null> => {
      const { data } = (await supabase
        .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
        .select("minutes, title")
        .eq("id", meeting.id)
        .maybeSingle()) as { data: ResultData | null };
      return data;
    };

    const tick = async () => {
      const { data } = (await supabase
        .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
        .select("status, updated_at, email_sent, email_sent_at, notes_path")
        .eq("id", meeting.id)
        .maybeSingle()) as { data: PollData | null };

      if (!data) {
        return;
      }

      const nextStatus = data.status as DbMeetingStatus;
      const notesJustReady =
        (nextStatus === "PROCESSING_DOCX" || nextStatus === "SENDING_EMAIL" || nextStatus === "PROCESSING_COMPLETE") &&
        meeting.status === "PROCESSING_NOTES";

        const shouldFetchResult =
          nextStatus !== meeting.status &&
          (nextStatus === "PROCESSING_DOCX" || nextStatus === "SENDING_EMAIL" || nextStatus === "PROCESSING_COMPLETE");
        const result = shouldFetchResult ? await fetchResult() : null;

        if (nextStatus === "FAILED") {
          // Generation failed — the kickoff snapshot never becomes the baseline.
          pendingDraftSnapshotRef.current = null;
        }

        setMeeting((current) =>
          current
            ? {
                ...current,
                status: nextStatus,
                updated_at: data.updated_at ?? current.updated_at,
                minutes: typeof result?.minutes === "string" ? result.minutes : current.minutes,
                title: typeof result?.title === "string" ? result.title : current.title,
                email_sent: Boolean(data.email_sent),
                email_sent_at: data.email_sent_at ?? current.email_sent_at,
                notes_path: typeof data.notes_path === "string" ? data.notes_path : current.notes_path,
              }
            : current
        );

        if (typeof data.notes_path === "string" && data.notes_path) {
          setNotesDocxPath(data.notes_path);
        }

        if (notesJustReady && typeof result?.minutes === "string") {
          const readyMinutes = result.minutes;
          setMinutesDraft(readyMinutes);
          // Draft generation succeeded — promote the kickoff snapshot to the
          // "last successful draft" baseline used by Retry.
          if (pendingDraftSnapshotRef.current) {
            lastDraftSnapshotRef.current = pendingDraftSnapshotRef.current;
            pendingDraftSnapshotRef.current = null;
          }
          toast.success(
            nextStatus === "PROCESSING_COMPLETE"
              ? "Draft ready."
              : "Draft ready. Creating DOCX in the background…"
          );

        if (isProjectRecording && !projectTitleContext) {
          return;
        }

        if (meeting.title_manually_edited) {
          return;
        }

        // Refine the title with a dedicated GPT call
        setIsGeneratingAutoTitle(true);
        void (async () => {
          try {
            const res = await fetch(
              `/api/meeting-recordings/${meeting.id}/generate-title`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  minutes: readyMinutes,
                  meetingContext: meeting.meeting_context ?? "",
                  projectName: projectTitleContext?.name,
                  projectCode: projectTitleContext?.code,
                  meetingDate: meeting.created_at,
                  layoutName: selectedLayout?.name,
                }),
              }
            );

            const payload = (await res.json()) as { title?: string; error?: string };

            if (!res.ok || !payload.title) {
              setIsGeneratingAutoTitle(false);
              return;
            }

            const { data: updatedTitle, error: titleUpdateError } = await supabase
              .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
              .update({ title: payload.title, updated_at: new Date().toISOString() })
              .eq("id", meeting.id)
              .eq("title_manually_edited", false)
              .select("title")
              .maybeSingle();

            if (titleUpdateError || !updatedTitle) {
              setIsGeneratingAutoTitle(false);
              return;
            }

            setMeeting((current) =>
              current ? { ...current, title: updatedTitle.title } : current
            );
            setTitleDraft(updatedTitle.title);
            setIsGeneratingAutoTitle(false);
            setIsAutoTitleAnimating(true);
          } catch {
            setIsGeneratingAutoTitle(false);
          }
        })();
      }
    };

    const interval = window.setInterval(() => {
      // No DB polling while the tab is hidden — the visibilitychange handler
      // catches up as soon as the tab becomes visible again.
      if (document.hidden) {
        return;
      }
      void tick();
    }, 8_000);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        void tick();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isProjectRecording, isProcessingInBackground, meeting, meeting?.id, meeting?.status, projectTitleContext, selectedLayout?.name]);

  useEffect(() => {
    if (!meeting) {
      return;
    }

    const hasUnsavedChanges =
      minutesDraft !== (meeting.minutes ?? "") ||
      attendeesDraftJson !== meetingAttendeesJson ||
      meetingContextDraft !== persistedMeetingContext ||
      meetingInstructionsDraft !== persistedMeetingInstructions ||
      previousMeetingNotesDraft !== persistedPreviousMeetingNotes ||
      previousMeetingIdDraft !== (meeting.previous_meeting_id ?? null) ||
      resolvedLayoutIdDraft !== persistedLayoutId;
    if (!hasUnsavedChanges) {
      return;
    }

    setManualSaveStatus("idle");
  }, [
    attendeesDraftJson,
    isProjectRecording,
    meeting,
    meetingAttendeesJson,
    meetingContextDraft,
    meetingInstructionsDraft,
    minutesDraft,
    persistedLayoutId,
    persistedPreviousMeetingNotes,
    previousMeetingNotesDraft,
    previousMeetingIdDraft,
    persistedMeetingContext,
    persistedMeetingInstructions,
    resolvedLayoutIdDraft,
  ]);

  useEffect(() => {
    if (!meeting) {
      setUserSearchResults([]);
      setIsSearchingUsers(false);
      return;
    }

    const query = findMentionQuery(attendeeInput);
    if (query === null) {
      setUserSearchResults([]);
      setIsSearchingUsers(false);
      return;
    }

    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      setIsSearchingUsers(true);

      const normalizedQuery = query.trim();
      const { data, error: searchError } = await supabase.rpc("search_users_for_mention", {
        search_term: normalizedQuery,
      });

      if (cancelled) {
        return;
      }

      if (searchError) {
        console.error("[MeetingMinutesPanel] search users:", searchError);
        setUserSearchResults([]);
        setIsSearchingUsers(false);
        return;
      }

      const selectedUserIds = new Set(
        attendeesDraft.map((attendee) => attendee.user_id).filter((value): value is string => Boolean(value))
      );

      setUserSearchResults(
        ((data as DbUserSearchUser[] | null) ?? []).filter((user) => !selectedUserIds.has(user.user_id))
      );
      setIsSearchingUsers(false);
    }, 180);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [attendeeInput, attendeesDraft, meeting]);

  useEffect(() => {
    if (minutesPanelWidth !== null) return;

    const container = containerRef.current;
    if (!container) return;

    const width = Math.round(container.clientWidth * 0.45);
    setMinutesPanelWidth(width);
  }, [minutesPanelWidth]);

  useEffect(() => {
    if (isGeneratingMinutes || isProcessingNotes || Boolean(minutesDraft.trim())) {
      setMinutesPanelOpen(true);
    }
  }, [isGeneratingMinutes, isProcessingNotes, minutesDraft]);

  const addInternalAttendee = useCallback((user: DbUserSearchUser) => {
    if (isMeetingDetailsEditingDisabled) {
      return;
    }

    const attendeeName = toUserSearchLabel(user);

    setAttendeesDraft((current) => {
      if (current.some((attendee) => attendee.user_id === user.user_id)) {
        return current;
      }

      return [
        ...current,
        {
          name: attendeeName,
          user_id: user.user_id,
        },
      ];
    });

    setAttendeeInput("");
    setUserSearchResults([]);
  }, [isMeetingDetailsEditingDisabled]);

  const addExternalAttendee = useCallback((rawValue: string) => {
    if (isMeetingDetailsEditingDisabled) {
      return;
    }

    const nextName = rawValue.trim();
    if (!nextName || nextName.startsWith("@")) {
      return;
    }

    setAttendeesDraft((current) => {
      const alreadyExists = current.some(
        (attendee) => attendee.user_id === null && attendee.name.toLowerCase() === nextName.toLowerCase()
      );
      if (alreadyExists) {
        return current;
      }

      return [
        ...current,
        {
          name: nextName,
          user_id: null,
        },
      ];
    });

    setAttendeeInput("");
    setUserSearchResults([]);
  }, [isMeetingDetailsEditingDisabled]);

  const removeAttendee = useCallback((index: number) => {
    if (isMeetingDetailsEditingDisabled) {
      return;
    }

    setAttendeesDraft((current) => current.filter((_, attendeeIndex) => attendeeIndex !== index));
  }, [isMeetingDetailsEditingDisabled]);

  const handleAttendeeInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (isMeetingDetailsEditingDisabled) {
        return;
      }

      if (event.key !== "Enter" && event.key !== ",") {
        if (event.key === "Backspace" && attendeeInput.length === 0 && attendeesDraft.length > 0) {
          event.preventDefault();
          removeAttendee(attendeesDraft.length - 1);
        }
        return;
      }

      event.preventDefault();

      const activeMentionQuery = findMentionQuery(attendeeInput);
      if (activeMentionQuery !== null && userSearchResults.length > 0) {
        addInternalAttendee(userSearchResults[0]);
        return;
      }

      addExternalAttendee(attendeeInput);
    },
    [
      addExternalAttendee,
      addInternalAttendee,
      attendeeInput,
      attendeesDraft.length,
      isMeetingDetailsEditingDisabled,
      removeAttendee,
      userSearchResults,
    ]
  );

  // previous_meeting_id is a same-table self-reference (each table's FK only
  // points at its own rows), so a project meeting can only ever link another
  // project meeting, and a personal meeting can only ever link another
  // personal one — offering the other table's items here would let someone
  // pick an option that can never actually be saved.
  const previousMeetingsGroups = useMemo<SearchDialogGroup[]>(() => {
    if (isProjectRecording) {
      const projectItems = siblingProjectMeetings
        .filter((entry) => entry.id !== meeting?.id)
        .map((entry) => ({
          id: entry.id,
          value: entry.title,
          label: entry.title || "Untitled meeting",
          icon: <AudioLinesIcon size={14} className="shrink-0 text-muted-foreground" />,
          meta: (
            <span className="ml-auto text-xs text-muted-foreground">
              {formatMeetingDate(entry.created_at)}
            </span>
          ),
        }));
      return [{ heading: "This project", items: projectItems }];
    }

    const homeItems = meetings
      .filter((entry) => entry.id !== meeting?.id)
      .map((entry) => ({
        id: entry.id,
        value: [entry.title, entry.meeting_context, entry.team_name]
          .filter(Boolean)
          .join(" "),
        label: entry.title || "Untitled meeting",
        icon: <AudioLinesIcon size={14} className="shrink-0 text-muted-foreground" />,
        meta: (
          <span className="ml-auto text-xs text-muted-foreground">
            {formatMeetingDate(entry.created_at)}
          </span>
        ),
      }));

    return [{ heading: "My meetings", items: homeItems }];
  }, [meeting?.id, meetings, isProjectRecording, siblingProjectMeetings]);

  const associatedPreviousMeeting = useMemo(
    () => meetings.find((entry) => entry.id === previousMeetingIdDraft) ?? null,
    [meetings, previousMeetingIdDraft]
  );

  useEffect(() => {
    if (!previousMeetingIdDraft) {
      setAssociatedPreviousMeetingTitle(null);
      return;
    }

    const inMemoryTitle = associatedPreviousMeeting?.title?.trim();
    if (inMemoryTitle) {
      setAssociatedPreviousMeetingTitle(inMemoryTitle);
      return;
    }

    let cancelled = false;

    const loadAssociatedMeetingTitle = async () => {
      const { data, error: loadError } = await supabase
        .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
        .select("id,title")
        .eq("id", previousMeetingIdDraft)
        .maybeSingle();

      if (cancelled) {
        return;
      }

      if (loadError || !data) {
        console.error("[MeetingMinutesPanel] load associated previous meeting:", loadError);
        setAssociatedPreviousMeetingTitle("Associated meeting");
        return;
      }

      setAssociatedPreviousMeetingTitle(data.title?.trim() || "Untitled meeting");
    };

    void loadAssociatedMeetingTitle();

    return () => {
      cancelled = true;
    };
  }, [associatedPreviousMeeting?.title, isProjectRecording, previousMeetingIdDraft]);

  useEffect(() => {
    if (!isAutoTitleAnimating) {
      return;
    }

    const renderedTitle = meeting?.title?.trim() || "Untitled meeting";
    const timeout = window.setTimeout(() => {
      setIsAutoTitleAnimating(false);
    }, Math.max(
      TITLE_ANIMATION_MIN_DURATION,
      Array.from(renderedTitle).length * MEETING_TITLE_TYPING_SPEED + TITLE_ANIMATION_BUFFER
    ));

    return () => {
      window.clearTimeout(timeout);
    };
  }, [isAutoTitleAnimating, meeting?.title]);

  useEffect(() => {
    if (!animateAssociatedPreviousMeetingTitle || !associatedPreviousMeetingTitle) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setAnimateAssociatedPreviousMeetingTitle(false);
    }, Math.min(2200, Math.max(700, associatedPreviousMeetingTitle.length * 24)));

    return () => {
      window.clearTimeout(timeout);
    };
  }, [animateAssociatedPreviousMeetingTitle, associatedPreviousMeetingTitle]);

  const handleOpenImportDialog = useCallback(() => {
    if (isMeetingDetailsEditingDisabled) {
      return;
    }

    setIsImportDialogOpen(true);
  }, [isMeetingDetailsEditingDisabled]);

  const handleSelectPreviousMeeting = useCallback(
    async (meetingId: string) => {
      if (isMeetingDetailsEditingDisabled) {
        return;
      }

      const { data, error: loadError } = await supabase
        .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
        .select("id,minutes,attendees")
        .eq("id", meetingId)
        .maybeSingle();

      if (loadError || !data) {
        console.error("[MeetingMinutesPanel] load previous meeting:", loadError);
        toast.error("Failed to load notes from the selected previous meeting.");
        return;
      }

      setPreviousMeetingIdDraft(meetingId);
      setPreviousMeetingNotesDraft(data.minutes?.trim() ?? "");
      setPreviousNotesUploadStatus(data.minutes?.trim() ? "done" : "idle");
      setPreviousNotesFileName(null);
      setAnimateAssociatedPreviousMeetingTitle(true);
      setIsImportDialogOpen(false);

      if (!data.minutes?.trim()) {
        toast.success("Previous meeting associated. No meeting notes found.");
        return;
      }

      toast.success("Previous meeting associated — notes loaded for generation context.");
    },
    [isMeetingDetailsEditingDisabled, isProjectRecording]
  );

  const handleCopyPreviousMeetingAttendees = useCallback(
    async () => {
      if (!previousMeetingIdDraft || isMeetingDetailsEditingDisabled) {
        return;
      }

      const { data, error: loadError } = await supabase
        .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
        .select("id,attendees")
        .eq("id", previousMeetingIdDraft)
        .maybeSingle();

      if (loadError || !data) {
        console.error("[MeetingMinutesPanel] load previous meeting attendees:", loadError);
        toast.error("Failed to load attendees from the selected previous meeting.");
        return;
      }

      const previousAttendees = normalizeAttendees(data.attendees);
      if (previousAttendees.length === 0) {
        toast.info("No attendees to copy from the selected meeting.");
        return;
      }

      const mergedAttendees = mergeMeetingAttendees(attendeesDraft, previousAttendees);
      const copiedAttendeeCount = mergedAttendees.length - attendeesDraft.length;
      if (copiedAttendeeCount === 0) {
        toast.info("All attendees from the selected meeting are already added.");
        return;
      }

      setAttendeesDraft(mergedAttendees);
      toast.success(
        `Copied ${copiedAttendeeCount} attendee${copiedAttendeeCount === 1 ? "" : "s"} from the selected meeting.`
      );
    },
    [attendeesDraft, isMeetingDetailsEditingDisabled, isProjectRecording, previousMeetingIdDraft]
  );

  const handleClearPreviousMeeting = useCallback(() => {
    setPreviousMeetingIdDraft(null);
    setPreviousMeetingNotesDraft("");
    setPreviousNotesUploadStatus("idle");
    setPreviousNotesFileName(null);
    setAnimateAssociatedPreviousMeetingTitle(false);
    toast.success("Previous meeting association cleared.");
  }, []);

  const handlePreviousNotesUpload = useCallback(async (file: File) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      toast.error("Session expired. Please sign in again.");
      return;
    }

    setPreviousNotesUploadStatus("uploading");
    setPreviousNotesFileName(file.name);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch("/api/meeting-recordings/parse-previous-notes", {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: formData,
      });

      const rawPayload: unknown = await response.json();
      const payload =
        rawPayload && typeof rawPayload === "object"
          ? (rawPayload as Record<string, unknown>)
          : {};
      const errorMessage = typeof payload.error === "string" ? payload.error : undefined;
      const parsedText = typeof payload.text === "string" ? payload.text : undefined;

      if (!response.ok) {
        setPreviousNotesUploadStatus("error");
        toast.error(errorMessage ?? "Failed to parse the uploaded file.");
        return;
      }

      setPreviousMeetingNotesDraft(parsedText ?? "");
      setPreviousNotesUploadStatus("done");
    } catch {
      setPreviousNotesUploadStatus("error");
      toast.error("Failed to upload the file. Please try again.");
    }
  }, []);

  const handleClearPreviousNotes = useCallback(() => {
    setPreviousMeetingNotesDraft("");
    setPreviousNotesUploadStatus("idle");
    setPreviousNotesFileName(null);
  }, []);

  const updateFeedback = useCallback(
    async (nextFeedback: "positive" | "negative" | null) => {
      if (!meeting) {
        return;
      }

      if (isFeedbackUpdating) {
        return;
      }

      const previousFeedback = feedback;
      setIsFeedbackUpdating(true);
      setFeedback(nextFeedback);

      const { error: updateError } = await supabase
        .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
        .update({ feedback: nextFeedback })
        .eq("id", meeting.id);

      if (updateError) {
        console.error("[MeetingMinutesPanel] update feedback:", updateError);
        setFeedback(previousFeedback);
        setIsFeedbackUpdating(false);
        toast.error("Failed to update meeting feedback.");
        return;
      }

      setMeeting((current) =>
        current
          ? {
              ...current,
              feedback: nextFeedback,
            }
          : current
      );

      setIsFeedbackUpdating(false);

      if (nextFeedback === null) {
        toast.success("Feedback removed.");
      } else if (nextFeedback === "positive") {
        toast.success("Positive feedback recorded.");
      } else {
        toast.success("Negative feedback recorded.");
      }
    },
    [feedback, isFeedbackUpdating, isProjectRecording, meeting]
  );

  const handleLike = useCallback(() => {
    void updateFeedback(feedback === "positive" ? null : "positive");
  }, [feedback, updateFeedback]);

  const handleDislike = useCallback(() => {
    void updateFeedback(feedback === "negative" ? null : "negative");
  }, [feedback, updateFeedback]);

  const getCurrentDurationSeconds = useCallback(() => {
    if (!recordingStartedAtRef.current) {
      return baseDurationSecondsRef.current;
    }

    const elapsed = Math.max(0, Math.floor((Date.now() - recordingStartedAtRef.current) / 1000));
    return baseDurationSecondsRef.current + elapsed;
  }, []);

  const stopRecorderAndStream = useCallback(async () => {
    recordingActiveRef.current = false;

    if (segmentTimeoutRef.current) {
      window.clearTimeout(segmentTimeoutRef.current);
      segmentTimeoutRef.current = null;
    }

    const recorder = mediaRecorderRef.current;

    if (recorder && recorder.state !== "inactive") {
      await new Promise<void>((resolve) => {
        const handleStop = () => {
          recorder.removeEventListener("stop", handleStop);
          resolve();
        };

        recorder.addEventListener("stop", handleStop);
        recorder.stop();
      });
    }

    mediaRecorderRef.current = null;

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    if (displayStreamRef.current) {
      displayStreamRef.current.getTracks().forEach((track) => track.stop());
      displayStreamRef.current = null;
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }

    if (audioContextRef.current) {
      void audioContextRef.current.close();
      audioContextRef.current = null;
    }
    setLiveAnalyser(null);
    setLiveCallAnalyser(null);

    const finalDuration = getCurrentDurationSeconds();
    baseDurationSecondsRef.current = finalDuration;
    recordingStartedAtRef.current = null;
    setLiveDurationSeconds(finalDuration);
    setIsPlaybackRunning(false);
    setIsRecordingPaused(false);

    // Release screen wake lock
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  }, [getCurrentDurationSeconds]);

  const persistDraftSnapshot = useCallback(async () => {
    if (!meeting || draftRowMissingRef.current) {
      return;
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.access_token) {
      throw new Error("Session expired while saving meeting draft.");
    }

    const response = await fetch("/api/meeting-recordings/save-draft", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        teamId: meeting.team_id,
        ...(isProjectRecording
          ? { projectRecordingId: meeting.id }
          : { recordingId: meeting.id }),
        transcript: transcriptRef.current,
        context: meetingContextDraft,
        instructions: meetingInstructionsDraft,
        previousMeetingNotes: previousMeetingNotesDraft,
        durationSeconds: getCurrentDurationSeconds(),
        transcribedChunks: transcribedChunksRef.current,
        failedChunks: failedChunksRef.current,
        attendees: attendeesDraft,
        previousMeetingId: previousMeetingIdDraft,
        layoutId: resolvedLayoutIdDraft,
      }),
    });

    const payload = (await response.json()) as { error?: string; missing?: boolean };

    if (response.status === 404 || payload.missing) {
      // The meeting no longer exists. Stop capturing rather than keep posting
      // a draft that has nowhere to land.
      draftRowMissingRef.current = true;
      await stopRecorderAndStream();
      toast.error("This meeting no longer exists — recording stopped and the draft was not saved.");
      return;
    }

    if (!response.ok) {
      throw new Error(payload.error || "Failed to save draft meeting recording.");
    }
  }, [
    attendeesDraft,
    getCurrentDurationSeconds,
    isProjectRecording,
    meeting,
    meetingContextDraft,
    meetingInstructionsDraft,
    previousMeetingNotesDraft,
    previousMeetingIdDraft,
    resolvedLayoutIdDraft,
    stopRecorderAndStream,
  ]);

  const waitForQueuedTranscriptions = useCallback(async () => {
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, 0);
    });

    while (pendingTranscriptionCountRef.current > 0) {
      await queuedChunkChainRef.current.catch(() => undefined);
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 0);
      });
    }

    await queuedChunkChainRef.current.catch(() => undefined);
  }, []);

  const persistRecordingCompleteStatus = useCallback(async (recordingId: string): Promise<DbMeetingStatus> => {
    const timestamp = new Date().toISOString();

    const table = isProjectRecording ? "project_meeting_recordings" : "meeting_recordings";

    const primary = await supabase
      .from(table)
      .update({ status: "RECORDING_COMPLETE", updated_at: timestamp })
      .eq("id", recordingId);

    if (!primary.error) {
      return "RECORDING_COMPLETE";
    }

    if (primary.error.code !== "22P02") {
      throw primary.error;
    }

    const legacy = await supabase
      .from(table)
      .update({ status: "RECORDING_COMPLETED", updated_at: timestamp })
      .eq("id", recordingId);

    if (!legacy.error) {
      return "RECORDING_COMPLETED";
    }

    if (legacy.error.code !== "22P02") {
      throw legacy.error;
    }

    const fallback = await supabase
      .from(table)
      .update({ status: "RECORDING_NOT_STARTED", updated_at: timestamp })
      .eq("id", recordingId);

    if (fallback.error) {
      throw fallback.error;
    }

    return "RECORDING_NOT_STARTED";
  }, [isProjectRecording]);

  // Rolls the row back to idle after a recording that captured nothing. Without
  // this the row stays at RECORDING once the recorder has already been torn down.
  const persistRecordingNotStartedStatus = useCallback(async (recordingId: string) => {
    const { error } = await supabase
      .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
      .update({ status: "RECORDING_NOT_STARTED", updated_at: new Date().toISOString() })
      .eq("id", recordingId);

    if (error) {
      throw error;
    }
  }, [isProjectRecording]);

  const transcribeChunk = useCallback(async (audioChunk: Blob, mimeTypeHint?: string, durationSeconds?: number) => {
    const mimeType = normalizeMimeType(audioChunk.type || mimeTypeHint || "audio/webm");
    const fileExtension = getAudioFileExtension(mimeType);
    const fileName = `meeting-chunk-${Date.now()}.${fileExtension}`;
    const chunkFile = new File([audioChunk], fileName, { type: mimeType || "audio/webm" });
    const formData = new FormData();
    formData.append("audio", chunkFile);
    if (activeId) formData.append("recordingId", activeId);
    if (typeof durationSeconds === "number" && durationSeconds > 0) {
      formData.append("durationSeconds", String(durationSeconds));
    }

    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch("/api/meeting-recordings/transcribe", {
      method: "POST",
      headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined,
      body: formData,
    });

    const payload = (await response.json()) as TranscribeChunkResponse;

    if (!response.ok) {
      throw new Error(payload.error || "Failed to transcribe meeting audio chunk.");
    }

    return typeof payload.text === "string" ? payload.text.trim() : "";
  }, [activeId]);

  const sendChunkFailureEmail = useCallback(async () => {
    try {
      await supabase.functions.invoke("send-recording-error-email", { body: {} });
    } catch (err) {
      console.error("[MeetingMinutesPanel] send chunk failure email:", err);
    }
  }, []);

  const enqueueChunkTranscription = useCallback(
    (audioChunk: Blob, mimeTypeHint?: string, durationSeconds?: number) => {
      if (!audioChunk.size || audioChunk.size < MIN_TRANSCRIBABLE_CHUNK_BYTES) {
        return;
      }

      pendingTranscriptionCountRef.current += 1;
      setPendingTranscriptionCount((count) => count + 1);

      queuedChunkChainRef.current = queuedChunkChainRef.current
        .catch(() => undefined)
        .then(async () => {
          try {
            const chunkText = await transcribeChunk(audioChunk, mimeTypeHint, durationSeconds);

            if (!chunkText) {
              failedChunksRef.current += 1;
              consecutiveFailedChunksRef.current += 1;
              const consecutive = consecutiveFailedChunksRef.current;
              if (consecutive >= 4) setShowChunkFailureBanner(true);
              if (FAILURE_EMAIL_THRESHOLDS.has(consecutive) && !notifiedFailureThresholdsRef.current.has(consecutive)) {
                notifiedFailureThresholdsRef.current.add(consecutive);
                void sendChunkFailureEmail();
              }
            } else {
              consecutiveFailedChunksRef.current = 0;
              setShowChunkFailureBanner(false);
              transcribedChunksRef.current += 1;
              transcriptRef.current = transcriptRef.current
                ? `${transcriptRef.current}\n${chunkText}`
                : chunkText;
              setLatestTranscribedText(chunkText);
              setLiveTranscript(transcriptRef.current);
            }

            setMeeting((current) =>
              current
                ? {
                    ...current,
                    transcript: transcriptRef.current,
                    transcribed_chunks: transcribedChunksRef.current,
                    failed_chunks: failedChunksRef.current,
                    duration_seconds: getCurrentDurationSeconds(),
                  }
                : current
            );

            await persistDraftSnapshot();
          } catch (transcriptionError) {
            failedChunksRef.current += 1;
            consecutiveFailedChunksRef.current += 1;
            const consecutive = consecutiveFailedChunksRef.current;
            if (consecutive >= 4) setShowChunkFailureBanner(true);
            if (FAILURE_EMAIL_THRESHOLDS.has(consecutive) && !notifiedFailureThresholdsRef.current.has(consecutive)) {
              notifiedFailureThresholdsRef.current.add(consecutive);
              void sendChunkFailureEmail();
            }
            setMeeting((current) =>
              current
                ? {
                    ...current,
                    failed_chunks: failedChunksRef.current,
                    duration_seconds: getCurrentDurationSeconds(),
                  }
                : current
            );
            await persistDraftSnapshot();

            console.error("[MeetingMinutesPanel] transcribe chunk:", transcriptionError);

            if (!transcriptionErrorShownRef.current) {
              transcriptionErrorShownRef.current = true;
              const message =
                transcriptionError instanceof Error
                  ? transcriptionError.message
                  : "Failed to transcribe audio chunk.";
              toast.error(message);
            }
          } finally {
            pendingTranscriptionCountRef.current = Math.max(0, pendingTranscriptionCountRef.current - 1);
            setPendingTranscriptionCount((count) => Math.max(0, count - 1));
          }
        });
    },
    [getCurrentDurationSeconds, persistDraftSnapshot, sendChunkFailureEmail, transcribeChunk]
  );

  const createAndStartRecorder = useCallback(
    (stream: MediaStream, mimeType?: string) => {
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      recorder.addEventListener("dataavailable", (event) => {
        const chunkDurationSeconds = (Date.now() - segmentStartedAtRef.current) / 1000;
        enqueueChunkTranscription(event.data, recorder.mimeType || undefined, chunkDurationSeconds);
      });

      recorder.addEventListener("error", () => {
        toast.error("Recording stopped due to a microphone error.");
      });

      recorder.addEventListener("stop", () => {
        if (!recordingActiveRef.current) {
          return;
        }

        const nextStream = mediaStreamRef.current;
        if (!nextStream) {
          return;
        }

        try {
          createAndStartRecorder(nextStream, recorderMimeTypeRef.current || mimeType);
        } catch (segmentError) {
          console.error("[MeetingMinutesPanel] restart segment:", segmentError);
          toast.error("Recording segment restart failed.");
          recordingActiveRef.current = false;
          setIsPlaybackRunning(false);
        }
      });

      recorder.start();
      mediaRecorderRef.current = recorder;
      segmentStartedAtRef.current = Date.now();

      if (segmentTimeoutRef.current) {
        window.clearTimeout(segmentTimeoutRef.current);
      }

      segmentTimeoutRef.current = window.setTimeout(() => {
        if (recorder.state !== "inactive") {
          recorder.stop();
        }
      }, CHUNK_TIMESLICE_MS);

      return recorder;
    },
    [enqueueChunkTranscription]
  );

  const handleStartRecording = useCallback(async () => {
    if (!meeting || isRecordingLocked || isGeneratingMinutes || anotherRecordingActive) {
      return;
    }

    if (typeof window === "undefined" || !window.isSecureContext) {
      toast.error("Microphone recording requires a secure browser context.");
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("This browser does not support microphone recording.");
      return;
    }

    try {
      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = micStream;

      let recordingStream: MediaStream = micStream;
      let mixAudioCtx: AudioContext | null = null;
      let mixAnalyserNode: AnalyserNode | null = null;

      // If "include call audio" is enabled, acquire display media and mix with mic
      if (includeCallAudio && navigator.mediaDevices?.getDisplayMedia) {
        try {
          const displayStream = await navigator.mediaDevices.getDisplayMedia({
            audio: true,
            video: true, // required by most browsers to show the audio checkbox
          });

          // We only need the audio — stop video tracks immediately
          displayStream.getVideoTracks().forEach((t) => t.stop());
          displayStreamRef.current = displayStream;

          if (displayStream.getAudioTracks().length > 0) {
            mixAudioCtx = new AudioContext();
            const dest = mixAudioCtx.createMediaStreamDestination();
            const micSource = mixAudioCtx.createMediaStreamSource(micStream);
            micSource.connect(dest);
            const displaySource = mixAudioCtx.createMediaStreamSource(displayStream);
            displaySource.connect(dest);

            // Analyser on the mic source for the mic waveform visualiser
            const analyser = mixAudioCtx.createAnalyser();
            analyser.fftSize = 256;
            micSource.connect(analyser);
            mixAnalyserNode = analyser;

            // Separate analyser for the call audio waveform visualiser
            const callAnalyser = mixAudioCtx.createAnalyser();
            callAnalyser.fftSize = 256;
            displaySource.connect(callAnalyser);
            setLiveCallAnalyser(callAnalyser);

            recordingStream = dest.stream;
          }
        } catch (displayError) {
          // User cancelled the share picker or browser denied — fall back to mic only
          console.warn("[MeetingMinutesPanel] display media not available, falling back to mic only:", displayError);
          if (displayStreamRef.current) {
            displayStreamRef.current.getTracks().forEach((t) => t.stop());
            displayStreamRef.current = null;
          }
        }
      }

      const supportedMimeType = getSupportedRecorderMimeType();

      if (!supportedMimeType) {
        throw new Error(
          "This browser does not support stable audio chunk recording. Use Chrome or Edge for now."
        );
      }

      recorderMimeTypeRef.current = supportedMimeType;
      recordingActiveRef.current = true;
      mediaStreamRef.current = recordingStream;
      createAndStartRecorder(recordingStream, supportedMimeType);

      // .then() is required — Supabase queries are lazy and only execute when then() is called
      void supabase
        .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
        .update({ status: "RECORDING", updated_at: new Date().toISOString() })
        .eq("id", meeting.id)
        .then();
      setMeeting((current) => current ? { ...current, status: "RECORDING" } : current);

      if (mixAudioCtx && mixAnalyserNode) {
        // AudioContext already created for mixing — reuse it for the visualiser
        audioContextRef.current = mixAudioCtx;
        setLiveAnalyser(mixAnalyserNode);
      } else {
        try {
          const audioCtx = new AudioContext();
          const source = audioCtx.createMediaStreamSource(micStream);
          const analyserNode = audioCtx.createAnalyser();
          analyserNode.fftSize = 256;
          source.connect(analyserNode);
          audioContextRef.current = audioCtx;
          setLiveAnalyser(analyserNode);
        } catch {
          // non-critical — waveform visualizer won't show
        }
      }

      recordingStartedAtRef.current = Date.now();
      setIsPlaybackRunning(true);
      setIsRecordingPaused(false);
      setLiveDurationSeconds(getCurrentDurationSeconds());

      // Request screen wake lock to prevent auto-lock interrupting recording on mobile
      if ("wakeLock" in navigator) {
        navigator.wakeLock.request("screen").then((lock) => {
          wakeLockRef.current = lock;
        }).catch(() => {
          // Non-critical — screen may still auto-lock during recording
        });
      }

      const callAudioActive = includeCallAudio && displayStreamRef.current !== null;
      toast.success(isRecordingInterrupted
        ? callAudioActive ? "Recording resumed with call audio." : "Recording resumed."
        : callAudioActive ? "Recording started with call audio." : "Recording started.");
    } catch (startError) {
      recordingActiveRef.current = false;
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((t) => t.stop());
        micStreamRef.current = null;
      }
      if (displayStreamRef.current) {
        displayStreamRef.current.getTracks().forEach((t) => t.stop());
        displayStreamRef.current = null;
      }
      console.error("[MeetingMinutesPanel] start recording:", startError);
      toast.error("Unable to access microphone.");
    }
  }, [anotherRecordingActive, createAndStartRecorder, getCurrentDurationSeconds, includeCallAudio, isProjectRecording, isRecordingInterrupted, isRecordingLocked, isGeneratingMinutes, meeting]);

  useEffect(() => {
    if (!isPlaybackRunning) {
      return;
    }

    const interval = window.setInterval(() => {
      setLiveDurationSeconds(getCurrentDurationSeconds());
    }, 1_000);

    return () => {
      window.clearInterval(interval);
    };
  }, [getCurrentDurationSeconds, isPlaybackRunning]);

  useEffect(() => {
    if (!isPlaybackRunning) return;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [isPlaybackRunning]);

  useEffect(() => {
    return () => {
      recordingActiveRef.current = false;

      if (segmentTimeoutRef.current) {
        window.clearTimeout(segmentTimeoutRef.current);
        segmentTimeoutRef.current = null;
      }

      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      }

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }

      if (displayStreamRef.current) {
        displayStreamRef.current.getTracks().forEach((track) => track.stop());
        displayStreamRef.current = null;
      }

      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
        micStreamRef.current = null;
      }

      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, []);

  // Re-acquire wake lock when page becomes visible again (e.g. after user returns from another app)
  // and warn if recording was interrupted while the screen was locked.
  useEffect(() => {
    if (!isPlaybackRunning) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && recordingActiveRef.current) {
        // Check if the recorder stopped while the page was hidden (e.g. screen locked on iOS)
        const recorder = mediaRecorderRef.current;
        if (recorder && recorder.state === "inactive") {
          toast.warning("Recording was interrupted when the screen locked. Tap Stop, then start a new recording to continue.");
        }

        // Re-acquire wake lock now that the page is visible again
        if ("wakeLock" in navigator) {
          navigator.wakeLock.request("screen").then((lock) => {
            wakeLockRef.current = lock;
          }).catch(() => {});
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isPlaybackRunning]);

  const handleToggleMute = useCallback(() => {
    if (!isPlaybackRunning) {
      return;
    }

    const stream = mediaStreamRef.current;
    if (!stream) {
      return;
    }

    // When mixing call audio, the recording stream is an AudioContext destination whose
    // virtual tracks cannot be toggled. Target the original mic stream instead.
    const muteStream = micStreamRef.current ?? stream;
    const tracks = muteStream.getAudioTracks();
    if (tracks.length === 0) {
      toast.error("No microphone track found.");
      return;
    }

    const nextMuted = !isRecordingPaused;
    tracks.forEach((track) => {
      track.enabled = !nextMuted;
    });

    setIsRecordingPaused(nextMuted);
    toast.success(nextMuted ? "Microphone muted." : "Microphone unmuted.");
  }, [isPlaybackRunning, isRecordingPaused]);

  const handleStopMeeting = useCallback(async () => {
    if (!meeting || isStoppingRecording || !isPlaybackRunning) {
      return;
    }

    setIsStoppingRecording(true);

    try {
      await stopRecorderAndStream();
      await waitForQueuedTranscriptions();
      await persistDraftSnapshot();

      const transcript = transcriptRef.current.trim();
      if (!transcript) {
        toast.warning("No speech was captured. Check your microphone and try recording again.");
        // The recorder is already stopped, so the row must not stay at RECORDING.
        try {
          await persistRecordingNotStartedStatus(meeting.id);
          setMeeting((current) => current ? { ...current, status: "RECORDING_NOT_STARTED" } : current);
        } catch (statusErr) {
          console.error("[MeetingMinutesPanel] reset status after empty transcript:", statusErr);
        }
        return;
      }

      // Recording stopped — move to the intermediate completed state before note generation starts
      const persistedStatus = await persistRecordingCompleteStatus(meeting.id);
      setMeeting((current) => current ? { ...current, status: persistedStatus } : current);
    } catch (err) {
      console.error("[MeetingMinutesPanel] handleStopMeeting:", err);
      toast.error(err instanceof Error ? err.message : "Failed to stop recording.");
    } finally {
      setIsStoppingRecording(false);
    }
  }, [
    isStoppingRecording,
    isPlaybackRunning,
    meeting,
    persistRecordingCompleteStatus,
    persistRecordingNotStartedStatus,
    persistDraftSnapshot,
    stopRecorderAndStream,
    waitForQueuedTranscriptions,
  ]);

  // Kick off minutes generation from the retained transcript. Shared by
  // Stop & Draft (first draft) and Retry (re-draft) — the transcript is NOT
  // deleted after drafting; it is kept for re-drafting and only cleared by
  // send-meeting-minutes-email after a successful send.
  const startDraftGeneration = useCallback(async () => {
    if (!meeting || isGeneratingMinutes) {
      return;
    }

    setIsGeneratingMinutes(true);

    try {
      await persistDraftSnapshot();
      const startedAt = new Date().toISOString();

      // Mark as processing in DB
      await supabase
        .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
        .update({ status: "PROCESSING_NOTES", updated_at: startedAt })
        .eq("id", meeting.id);

      // Fire-and-forget — the edge function generates minutes and saves to
      // DB; the status poll above picks up completion and shows "Draft ready."
      void supabase.functions.invoke("generate-meeting-minutes", {
        body: isProjectRecording
          ? { projectRecordingId: meeting.id }
          : { recordingId: meeting.id, teamId: meeting.team_id },
      });

      // Snapshot the inputs this draft is generated from; the poll promotes
      // it to the Retry baseline only when the generation succeeds.
      pendingDraftSnapshotRef.current = createMeetingDraftSnapshot({
        meetingContext: meetingContextDraft,
        meetingInstructions: meetingInstructionsDraft,
        attendees: attendeesDraft,
        previousMeetingId: previousMeetingIdDraft,
        previousMeetingNotes: previousMeetingNotesDraft,
        layoutId: resolvedLayoutIdDraft,
      });

      setMeeting((current) =>
        current ? { ...current, status: "PROCESSING_NOTES", updated_at: startedAt } : current
      );
      toast.info("Draft in progress…");
    } catch (err) {
      console.error("[MeetingMinutesPanel] startDraftGeneration:", err);
      toast.error(err instanceof Error ? err.message : "Failed to start minutes generation.");
    } finally {
      setIsGeneratingMinutes(false);
    }
  }, [
    attendeesDraft,
    isGeneratingMinutes,
    isProjectRecording,
    meeting,
    meetingContextDraft,
    meetingInstructionsDraft,
    persistDraftSnapshot,
    previousMeetingIdDraft,
    previousMeetingNotesDraft,
    resolvedLayoutIdDraft,
  ]);

  // Stop & Draft — stops the recording and immediately sends the captured
  // transcript for minutes generation.
  const handleStopAndDraft = useCallback(async () => {
    if (!meeting || isStoppingRecording || !isPlaybackRunning) {
      return;
    }

    setIsStoppingRecording(true);

    try {
      await stopRecorderAndStream();
      await waitForQueuedTranscriptions();
      await persistDraftSnapshot();

      const transcript = transcriptRef.current.trim();
      if (!transcript) {
        toast.warning("No speech was captured. Check your microphone and try recording again.");
        // The recorder is already stopped, so the row must not stay at RECORDING.
        try {
          await persistRecordingNotStartedStatus(meeting.id);
          setMeeting((current) => current ? { ...current, status: "RECORDING_NOT_STARTED" } : current);
        } catch (statusErr) {
          console.error("[MeetingMinutesPanel] reset status after empty transcript:", statusErr);
        }
        return;
      }

      // Recording stopped — move to the intermediate completed state before
      // drafting starts so a generation kickoff failure leaves a resumable state.
      const persistedStatus = await persistRecordingCompleteStatus(meeting.id);
      setMeeting((current) => current ? { ...current, status: persistedStatus } : current);

      await startDraftGeneration();
    } catch (err) {
      console.error("[MeetingMinutesPanel] handleStopAndDraft:", err);
      toast.error(err instanceof Error ? err.message : "Failed to stop recording.");
    } finally {
      setIsStoppingRecording(false);
    }
  }, [
    isStoppingRecording,
    isPlaybackRunning,
    meeting,
    persistRecordingCompleteStatus,
    persistRecordingNotStartedStatus,
    persistDraftSnapshot,
    startDraftGeneration,
    stopRecorderAndStream,
    waitForQueuedTranscriptions,
  ]);

  // Retry (re-draft) — regenerate the minutes from the retained transcript,
  // but only when a generation input actually changed since the last
  // successful draft. An unchanged meeting skips generation entirely.
  const handleRedraft = useCallback(async () => {
    if (!meeting || isGeneratingMinutes || isProcessingInBackground || isSendingEmail) {
      return;
    }

    const currentSnapshot = createMeetingDraftSnapshot({
      meetingContext: meetingContextDraft,
      meetingInstructions: meetingInstructionsDraft,
      attendees: attendeesDraft,
      previousMeetingId: previousMeetingIdDraft,
      previousMeetingNotes: previousMeetingNotesDraft,
      layoutId: resolvedLayoutIdDraft,
    });

    if (
      lastDraftSnapshotRef.current &&
      isMeetingDraftSnapshotEqual(currentSnapshot, lastDraftSnapshotRef.current)
    ) {
      // Nothing feeding generation changed — no API call, no token spend.
      setIsNoChangesDialogOpen(true);
      return;
    }

    await startDraftGeneration();
  }, [
    attendeesDraft,
    isGeneratingMinutes,
    isProcessingInBackground,
    isSendingEmail,
    meeting,
    meetingContextDraft,
    meetingInstructionsDraft,
    previousMeetingIdDraft,
    previousMeetingNotesDraft,
    resolvedLayoutIdDraft,
    startDraftGeneration,
  ]);

  const handleRetry = useCallback(async () => {
    if (!meeting) return;
    const hasNotes = Boolean(meeting.minutes?.trim());
    const nextStatus = hasNotes ? "PROCESSING_DOCX" : "PROCESSING_NOTES";
    const startedAt = new Date().toISOString();

    // Optimistic update — jump the UI immediately before the DB write returns
    setMeeting((current) =>
      current ? { ...current, status: nextStatus, updated_at: startedAt, ...(hasNotes ? { notes_path: null } : {}) } : current
    );
    if (hasNotes) setNotesDocxPath(null);

    const edgeFunctionBody = isProjectRecording
      ? { projectRecordingId: meeting.id }
      : { recordingId: meeting.id, teamId: meeting.team_id };

    await supabase
      .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
      .update({ status: nextStatus, ...(hasNotes ? { notes_path: null } : {}), updated_at: startedAt })
      .eq("id", meeting.id);

    if (hasNotes) {
      void supabase.functions.invoke("generate-meeting-notes-docx", {
        body: edgeFunctionBody,
      });
      toast.success("Retrying DOCX generation in the background.");
    } else {
      void supabase.functions.invoke("generate-meeting-minutes", {
        body: edgeFunctionBody,
      });
      // Snapshot the inputs this retry generates from; promoted to the
      // Retry baseline by the status poll when the draft succeeds.
      pendingDraftSnapshotRef.current = createMeetingDraftSnapshot({
        meetingContext: meetingContextDraft,
        meetingInstructions: meetingInstructionsDraft,
        attendees: attendeesDraft,
        previousMeetingId: previousMeetingIdDraft,
        previousMeetingNotes: previousMeetingNotesDraft,
        layoutId: resolvedLayoutIdDraft,
      });
      toast.info("Draft in progress…");
    }
  }, [
    attendeesDraft,
    isProjectRecording,
    meeting,
    meetingContextDraft,
    meetingInstructionsDraft,
    previousMeetingIdDraft,
    previousMeetingNotesDraft,
    resolvedLayoutIdDraft,
  ]);

  // Generate Docx and Send — generates the DOCX from the current draft, then
  // emails the notes with it attached. The DOCX is only ever generated here;
  // there is no automatic/background DOCX generation elsewhere. The edge
  // function clears the transcript ONLY after the SMTP send succeeds; on
  // failure the transcript and draft stay intact so the user can retry
  // without regenerating notes. `isSendingEmail` guards the whole round trip
  // so a double-click cannot queue a duplicate DOCX/email.
  const handleSendEmail = useCallback(async () => {
    if (
      !meeting ||
      isSendingEmail ||
      isGeneratingMinutes ||
      isProcessingInBackground
    ) {
      return;
    }

    const table = isProjectRecording ? "project_meeting_recordings" : "meeting_recordings";

    if (!meeting.minutes?.trim()) {
      toast.error("No minutes available. Generate a draft first.");
      return;
    }

    setIsSendingEmail(true);

    const previousStatus = meeting.status;
    const previousNotesDocxPath = notesDocxPath;
    const startedAt = new Date().toISOString();
    setMeeting((current) =>
      current ? { ...current, status: "PROCESSING_DOCX", notes_path: null, updated_at: startedAt } : current
    );
    setNotesDocxPath(null);

    try {
      await supabase
        .from(table)
        .update({ status: "PROCESSING_DOCX", notes_path: null, updated_at: startedAt })
        .eq("id", meeting.id);

      const { data: docxData, error: docxError } = await supabase.functions.invoke("generate-meeting-notes-docx", {
        body: isProjectRecording
          ? { projectRecordingId: meeting.id }
          : { recordingId: meeting.id, teamId: meeting.team_id },
      });

      if (docxError) {
        let message = "Failed to generate the DOCX.";
        try {
          const payload = (await (docxError as { context?: Response }).context?.json()) as
            | { error?: string }
            | undefined;
          if (payload && typeof payload.error === "string" && payload.error) {
            message = payload.error;
          }
        } catch {
          // keep the default message
        }
        throw new Error(message);
      }

      const notesPath =
        typeof (docxData as { notesPath?: string } | null)?.notesPath === "string"
          ? (docxData as { notesPath: string }).notesPath
          : null;
      setNotesDocxPath(notesPath);

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user?.email) {
        throw new Error("Please sign in again to send the minutes email.");
      }

      const sendingAt = new Date().toISOString();
      setMeeting((current) =>
        current ? { ...current, status: "SENDING_EMAIL", notes_path: notesPath, updated_at: sendingAt } : current
      );

      await supabase
        .from(table)
        .update({ status: "SENDING_EMAIL", updated_at: sendingAt })
        .eq("id", meeting.id);

      const { error: sendError } = await supabase.functions.invoke("send-meeting-minutes-email", {
        body: {
          to: user.email,
          fromName: "Woods AI - Meetings",
          ...(isProjectRecording
            ? { projectRecordingId: meeting.id }
            : { recordingId: meeting.id, teamId: meeting.team_id }),
        },
      });

      if (sendError) {
        let message = "Failed to send the minutes email.";
        try {
          const payload = (await (sendError as { context?: Response }).context?.json()) as
            | { error?: string }
            | undefined;
          if (payload && typeof payload.error === "string" && payload.error) {
            message = payload.error;
          }
        } catch {
          // keep the default message
        }
        throw new Error(message);
      }

      // Send succeeded — the edge function marked the meeting complete and
      // cleared the transcript (the only point transcripts are deleted).
      transcriptRef.current = "";
      setLiveTranscript("");
      setLatestTranscribedText("");
      setMeeting((current) =>
        current
          ? {
              ...current,
              status: "PROCESSING_COMPLETE",
              email_sent: true,
              email_sent_at: new Date().toISOString(),
              transcript: null,
            }
          : current
      );
      toast.success("Meeting minutes email sent.");
    } catch (err) {
      console.error("[MeetingMinutesPanel] handleSendEmail:", err);
      // Restore the pre-send state — transcript and draft are untouched, so
      // the user can simply retry.
      setNotesDocxPath(previousNotesDocxPath);
      setMeeting((current) =>
        current ? { ...current, status: previousStatus, notes_path: previousNotesDocxPath } : current
      );
      // .then() is required — Supabase queries are lazy and only execute when then() is called
      void supabase
        .from(table)
        .update({ status: previousStatus, notes_path: previousNotesDocxPath, updated_at: new Date().toISOString() })
        .eq("id", meeting.id)
        .then();
      toast.error(err instanceof Error ? err.message : "Failed to generate the DOCX and send the minutes email.");
    } finally {
      setIsSendingEmail(false);
    }
  }, [
    isGeneratingMinutes,
    isProcessingInBackground,
    isProjectRecording,
    isSendingEmail,
    meeting,
    notesDocxPath,
  ]);

  const handleDownload = useCallback(async () => {
    if (!meeting?.minutes?.trim()) {
      toast.error("No minutes available to download.");
      return;
    }

    if (!notesDocxPath) {
      toast.info("The DOCX is still being generated, please wait…");
      return;
    }

    const safeFilename =
      `${(meeting.title ?? "meeting-minutes").replace(/[<>:"/\\|?*]/g, "").trim() || "meeting-minutes"}.docx`;

    const { data: signed, error: signedError } = await supabase.storage
      .from("files")
      .createSignedUrl(notesDocxPath, 3600, { download: safeFilename });

    if (signedError || !signed?.signedUrl) {
      toast.error("Failed to prepare download link. Try again.");
      return;
    }

    const a = document.createElement("a");
    a.href = signed.signedUrl;
    a.download = safeFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [meeting, notesDocxPath]);

  const handleCopy = useCallback(async () => {
    const textToCopy = minutesDraft.trim();

    if (!textToCopy) {
      toast.error("No minutes available to copy.");
      return;
    }

    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
      toast.error("Clipboard is not available in this browser.");
      return;
    }

    try {
      await navigator.clipboard.writeText(textToCopy);

      toast.success("Minutes copied to clipboard.");
    } catch (copyError) {
      console.error("[MeetingMinutesPanel] copy minutes:", copyError);
      toast.error("Failed to copy minutes.");
    }
  }, [minutesDraft]);

  const handleMinutesDraftChange = useCallback((value: string) => {
    minutesEditDirtyRef.current = true;
    setMinutesDraft(value);
  }, []);

  const persistMinutes = useCallback(
    async (markdown: string) => {
      if (!meeting) return false;

      const { error: updateError } = await supabase
        .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
        .update({ minutes: markdown, updated_at: new Date().toISOString() })
        .eq("id", meeting.id);

      if (updateError) {
        console.error("[MeetingMinutesPanel] persist minutes:", updateError);
        return false;
      }

      setMeeting((current) => (current ? { ...current, minutes: markdown } : current));
      return true;
    },
    [isProjectRecording, meeting]
  );

  // Debounced autosave for manual edits in the markdown editor. Send and
  // download read `meeting.minutes` from the DB, so unsaved draft edits would
  // otherwise be silently lost. Gated on the dirty ref so drafts that lag
  // behind background generation never overwrite freshly generated minutes.
  useEffect(() => {
    if (!minutesEditDirtyRef.current || !meeting) return;
    if (minutesDraft === (meeting.minutes ?? "")) {
      minutesEditDirtyRef.current = false;
      return;
    }

    const timeout = window.setTimeout(() => {
      minutesEditDirtyRef.current = false;
      void persistMinutes(minutesDraft).then((persisted) => {
        if (!persisted) toast.error("Failed to save minutes edits.");
      });
    }, 1000);

    return () => window.clearTimeout(timeout);
  }, [meeting, minutesDraft, persistMinutes]);

  const handleUndoSelectionEdit = useCallback(async () => {
    if (!selectionEditUndo) return;

    const targetMarkdown = selectionEditUndo;
    const redoMarkdown = minutesDraft;
    setMinutesDraft(targetMarkdown);
    setSelectionEditUndo(null);
    setSelectionEditRedo(redoMarkdown);
    const persisted = await persistMinutes(targetMarkdown);
    toast[persisted ? "success" : "error"](
      persisted ? "Change undone." : "Reverted here, but failed to save it to the note."
    );
  }, [minutesDraft, persistMinutes, selectionEditUndo]);

  const handleRedoSelectionEdit = useCallback(async () => {
    if (!selectionEditRedo) return;

    const targetMarkdown = selectionEditRedo;
    const undoMarkdown = minutesDraft;
    setMinutesDraft(targetMarkdown);
    setSelectionEditRedo(null);
    setSelectionEditUndo(undoMarkdown);
    const persisted = await persistMinutes(targetMarkdown);
    toast[persisted ? "success" : "error"](
      persisted ? "Change redone." : "Reapplied here, but failed to save it to the note."
    );
  }, [minutesDraft, persistMinutes, selectionEditRedo]);

  const handleSelectionEdit = useCallback(
    async (selectedText: string, instruction: string) => {
      if (!meeting) return false;

      const previousMarkdown = minutesDraft;

      try {
        const response = await fetch("/api/meeting-recordings/edit-selection", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ markdown: minutesDraft, selectedText, instruction }),
        });
        const data = (await response.json().catch(() => null)) as
          | { updatedMarkdown?: string; error?: string }
          | null;

        if (!response.ok || !data?.updatedMarkdown) {
          throw new Error(data?.error || "Failed to apply the change.");
        }

        const updatedMarkdown = data.updatedMarkdown;
        // Update the draft (and therefore the markdown/preview views) right
        // away, then persist to the note itself — handleSendEmail and
        // handleDownload read `meeting.minutes` from the DB, not the local
        // draft, so an unsaved edit here would otherwise be silently lost.
        setMinutesDraft(updatedMarkdown);

        const persisted = await persistMinutes(updatedMarkdown);

        if (!persisted) {
          toast.error("Change applied, but failed to save it to the note.");
          return true;
        }

        setSelectionEditUndo(previousMarkdown);
        setSelectionEditRedo(null);
        toast.success("Selection updated.");
        return true;
      } catch (error) {
        console.error("[MeetingMinutesPanel] selection edit:", error);
        toast.error(error instanceof Error ? error.message : "Failed to apply the change.");
        return false;
      }
    },
    [meeting, minutesDraft, persistMinutes]
  );

  const handleStopAndNewMeeting = useCallback(async () => {
    if (!meeting) return;
    if (isPlaybackRunning && !isGeneratingMinutes) {
      await handleStopMeeting();
    }
    if (transcriptRef.current.trim()) {
      router.push("/meeting-minutes");
    } else {
      router.push("/meeting-minutes");
    }
  }, [handleStopMeeting, isGeneratingMinutes, isPlaybackRunning, meeting, router]);

  const handleCreateLinkedMeeting = useCallback(async () => {
    if (!meeting || isCreatingLinkedMeeting || !canCreateLinkedMeeting) {
      return;
    }

    setIsCreatingLinkedMeeting(true);

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
      console.error("[MeetingMinutesPanel] create linked meeting auth:", authError);
      toast.error("Please sign in to create a meeting.");
      setIsCreatingLinkedMeeting(false);
      return;
    }

    const nextTitle = `Follow-up: ${meeting.title || "Untitled meeting"}`.slice(0, 200);

    const { data: createdMeeting, error: createError } = await supabase
      .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
      .insert({
        user_id: user.id,
        team_id: meeting.team_id,
        ...(isProjectRecording ? { project_id: (meeting as { project_id?: string }).project_id } : {}),
        previous_meeting_id: meeting.id,
        title: nextTitle,
        meeting_context: "",
        transcript: null,
        minutes: "",
        layout_id: resolvedLayoutIdDraft,
      })
      .select("id")
      .single();

    if (createError || !createdMeeting) {
      console.error("[MeetingMinutesPanel] create linked meeting:", createError);
      toast.error("Failed to create a new linked meeting.");
      setIsCreatingLinkedMeeting(false);
      return;
    }

    toast.success("New linked meeting created.");
    setIsCreatingLinkedMeeting(false);
    router.push(`/meeting-minutes?recordingId=${createdMeeting.id}`);
  }, [canCreateLinkedMeeting, isCreatingLinkedMeeting, isProjectRecording, meeting, resolvedLayoutIdDraft, router]);

  const handleStartTitleEdit = useCallback(() => {
    if (!meeting || isSavingTitle || isDeletingMeeting) {
      return;
    }

    setTitleDraft(meeting.title ?? "");
    setIsTitleEditing(true);
  }, [isDeletingMeeting, isSavingTitle, meeting]);

  const handleSaveTitle = useCallback(async () => {
    if (!meeting || isSavingTitle) {
      return;
    }

    const nextTitle = titleDraft.trim();
    if (!nextTitle) {
      toast.error("Title cannot be empty.");
      return;
    }

    const currentTitle = (meeting.title ?? "").trim();
    if (nextTitle === currentTitle) {
      setIsTitleEditing(false);
      setTitleDraft(meeting.title ?? "");
      return;
    }

    setIsSavingTitle(true);

    const { error: updateError } = await supabase
      .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
      .update({
        title: nextTitle,
        title_manually_edited: true,
        updated_at: new Date().toISOString(),
      })
      .eq("id", meeting.id);

    if (updateError) {
      console.error("[MeetingMinutesPanel] update title:", updateError);
      toast.error("Failed to update meeting title.");
      setIsSavingTitle(false);
      return;
    }

    setMeeting((current) =>
      current
        ? {
            ...current,
            title: nextTitle,
            title_manually_edited: true,
          }
        : current
    );
    setTitleDraft(nextTitle);
    setIsTitleEditing(false);
    setIsSavingTitle(false);
    toast.success("Meeting title updated.");
  }, [isProjectRecording, isSavingTitle, meeting, titleDraft]);

  const handleTitleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        void handleSaveTitle();
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        setTitleDraft(meeting?.title ?? "");
        setIsTitleEditing(false);
      }
    },
    [handleSaveTitle, meeting?.title]
  );

  const handleDeleteMeeting = useCallback(async () => {
    if (!meeting || isDeletingMeeting) {
      return;
    }

    setIsDeletingMeeting(true);

    // Stop capturing and mark the row gone BEFORE deleting it: a recorder left
    // running would keep autosaving a draft whose row no longer exists.
    draftRowMissingRef.current = true;
    await stopRecorderAndStream();

    const { error: deleteError } = await supabase
      .from(isProjectRecording ? "project_meeting_recordings" : "meeting_recordings")
      .delete()
      .eq("id", meeting.id);

    if (deleteError) {
      console.error("[MeetingMinutesPanel] delete meeting:", deleteError);
      toast.error("Failed to delete meeting.");
      draftRowMissingRef.current = false;
      setIsDeletingMeeting(false);
      return;
    }

    toast.success("Meeting deleted.");
    setIsDeleteDialogOpen(false);
    setIsDeletingMeeting(false);
    router.push("/meeting-minutes");
  }, [isDeletingMeeting, isProjectRecording, meeting, router, stopRecorderAndStream]);

  const isStartDisabled = isRecordingLocked || isGeneratingMinutes || anotherRecordingActive;
  const hasGeneratedMinutes = Boolean(minutesDraft.trim());
  // Keeps the minutes editor mounted even when the user clears the draft mid-edit.
  const hasMinutesContent = hasGeneratedMinutes || Boolean(meeting?.minutes?.trim());
  const canGenerateNotes =
    !isPlaybackRunning &&
    !isGeneratingMinutes &&
    !isProcessingInBackground &&
    (isRecordingCompleted || isRecordingInterrupted) &&
    Boolean(meeting?.transcript?.trim() || liveTranscript.trim()) &&
    !hasGeneratedMinutes;
  const hasTranscriptActivity =
    Boolean(liveTranscript.trim()) ||
    pendingTranscriptionCount > 0 ||
    isPlaybackRunning ||
    isRecordingPaused ||
    isRecordingCompleted ||
    isRecordingInterrupted;

  const hasMinutesChanges = Boolean(meeting) && minutesDraft !== (meeting?.minutes ?? "");
  const hasMeetingContextChanges = Boolean(meeting) && meetingContextDraft !== persistedMeetingContext;
  const hasMeetingInstructionsChanges =
    Boolean(meeting) && meetingInstructionsDraft !== persistedMeetingInstructions;
  const hasAttendeesChanges = Boolean(meeting) && attendeesDraftJson !== meetingAttendeesJson;
  const hasPreviousNotesChanges = Boolean(meeting) && (
    previousMeetingNotesDraft !== persistedPreviousMeetingNotes ||
    previousMeetingIdDraft !== (meeting?.previous_meeting_id ?? null)
  );
  const hasLayoutChanges = Boolean(meeting) && resolvedLayoutIdDraft !== persistedLayoutId;
  const hasMeetingChanges =
    hasMinutesChanges ||
    hasAttendeesChanges ||
    hasMeetingContextChanges ||
    hasMeetingInstructionsChanges ||
    hasPreviousNotesChanges ||
    hasLayoutChanges;
  const showHeaderStatusIndicator = (!isProcessingInBackground || isGenerationHung) && !isPlaybackRunning;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        title={meeting ? (
          <div className="flex min-w-0 flex-col gap-1">
            {isProjectRecording && projectTitleContext?.name ? (
              <button
                className="inline-flex h-8 w-fit max-w-full items-center gap-1.5 rounded-full bg-muted px-3 text-sm font-medium text-primary transition-colors hover:bg-accent"
                onClick={() => router.push(`/projects/${meetingProjectId}`)}
                type="button"
              >
                <GalleryVerticalEndIcon size={14} className="text-primary" />
                <span className="truncate">{projectTitleContext.name}</span>
              </button>
            ) : null}
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {isTitleEditing ? (
                <Field className="w-full max-w-xl gap-1">
                  <FieldLabel className="sr-only" htmlFor="meeting-title">
                    Meeting title
                  </FieldLabel>
                  <Input
                    aria-label="Meeting title"
                    autoFocus
                    className="h-9 text-base sm:text-lg"
                    disabled={isSavingTitle || isDeletingMeeting || isGeneratingAutoTitle}
                    id="meeting-title"
                    maxLength={200}
                    onBlur={() => void handleSaveTitle()}
                    onChange={(event) => setTitleDraft(event.target.value)}
                    onKeyDown={handleTitleKeyDown}
                    value={titleDraft}
                  />
                </Field>
              ) : (
                <button
                  aria-label="Edit meeting title"
                  className="inline-flex max-w-full items-center rounded-md px-1 py-0.5 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  disabled={isSavingTitle || isDeletingMeeting || isGeneratingAutoTitle}
                  onClick={handleStartTitleEdit}
                  type="button"
                >
                  <span className="max-w-full truncate">
                    {isGeneratingAutoTitle ? (
                      <Shimmer as="span" className="block truncate" duration={1.6}>
                        {meeting.title?.trim() ? meeting.title : "Untitled meeting"}
                      </Shimmer>
                    ) : isAutoTitleAnimating ? (
                      <TypingAnimation
                        key={meeting.title?.trim() ? meeting.title : "Untitled meeting"}
                        as="span"
                        blinkCursor={false}
                        className="block truncate"
                        pauseDelay={0}
                        showCursor={false}
                        startOnView={false}
                        typeSpeed={MEETING_TITLE_TYPING_SPEED}
                        words={[meeting.title?.trim() ? meeting.title : "Untitled meeting"]}
                      />
                    ) : (
                      <span className="block truncate">{meeting.title?.trim() ? meeting.title : "Untitled meeting"}</span>
                    )}
                  </span>
                </button>
              )}
            </div>
          </div>
        ) : "Meeting minutes"}
        description={
          activeId
            ? "Review minutes, metadata, and feedback for this meeting recording."
            : "Select a meeting from the Meetings dialog to view its notes."
        }
        onBack={onBack}
        actions={meeting ? (
          <div className="flex w-full flex-wrap items-center justify-end gap-1.5 sm:gap-2">
            {showHeaderStatusIndicator ? (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      aria-label={meetingStatusPresentation.label}
                      className={cn(
                        "inline-flex size-9 items-center justify-center rounded-full border sm:hidden",
                        meetingStatusPresentation.toneClassName
                      )}
                      role="status"
                    >
                      {renderMeetingStatusIcon("size-4")}
                      <span className="sr-only">{meetingStatusPresentation.mobileLabel}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>{meetingStatusPresentation.label}</p>
                  </TooltipContent>
                </Tooltip>

                <Badge
                  variant="outline"
                  className={cn(
                    "hidden h-9 items-center gap-1.5 rounded-full px-3 text-sm font-normal sm:inline-flex",
                    meetingStatusPresentation.toneClassName
                  )}
                >
                  {renderMeetingStatusIcon("size-3.5")}
                  <span>{meetingStatusPresentation.label}</span>
                </Badge>
              </>
            ) : null}

            {isPlaybackRunning ? (
              <div className="inline-flex h-9 items-center gap-1.5 rounded-full border border-activated/40 bg-activated/10 px-3 text-sm text-activated">
                <div className={cn("size-2 rounded-full bg-activated animate-pulse animation-duration-[2s]", isRecordingPaused && "opacity-30")} />
                {isRecordingPaused ? "Muted" : "Recording"}
              </div>
            ) : null}

            {isProcessingInBackground && !isGenerationHung ? (
              <div className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-muted px-3 text-sm text-muted-foreground">
                <Spinner className="size-3.5" />
                {isProcessingNotes
                  ? "Generating minutes…"
                  : meeting?.status === "PROCESSING_DOCX"
                    ? "Creating notes.docx..."
                    : "Sending email…"}
              </div>
            ) : isGenerationFailed || isGenerationHung ? (
              <>
                <Button
                  className="h-9 rounded-full px-3 text-sm"
                  onClick={() => void handleRetry()}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  Retry
                </Button>
              </>
            ) : null}

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-background px-3 text-sm text-foreground">
                  <TimerIcon className="text-muted-foreground" size={14} />
                  {formatDuration(liveDurationSeconds)}
                  {(meeting?.failed_chunks ?? 0) > 0 ? (
                    <span className="text-destructive">
                      ({formatChunkDuration((meeting?.failed_chunks ?? 0) * 30)} failed)
                    </span>
                  ) : null}
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <div className="space-y-0.5 text-xs">
                  <p>Duration: {formatDuration(liveDurationSeconds)}</p>
                  <p>Transcribed: {formatChunkDuration((meeting?.transcribed_chunks ?? 0) * 30)}</p>
                  <p className={(meeting?.failed_chunks ?? 0) > 0 ? "text-destructive" : undefined}>
                    Failed: {formatChunkDuration((meeting?.failed_chunks ?? 0) * 30)}
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>

            <PolicyButton />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label={feedback === "positive" ? "Remove positive feedback" : "Add positive feedback"}
                  aria-pressed={feedback === "positive"}
                  className={cn(
                    "size-9",
                    feedback === "positive" && "bg-muted text-activated"
                  )}
                  disabled={isFeedbackUpdating}
                  onClick={handleLike}
                  size="icon"
                  type="button"
                  variant="outline"
                >
                  <UpvoteIcon className="text-current" size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>{feedback === "positive" ? "Remove like" : "Like"}</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label={feedback === "negative" ? "Remove negative feedback" : "Add negative feedback"}
                  aria-pressed={feedback === "negative"}
                  className={cn(
                    "size-9",
                    feedback === "negative" && "bg-muted text-destructive"
                  )}
                  disabled={isFeedbackUpdating}
                  onClick={handleDislike}
                  size="icon"
                  type="button"
                  variant="outline"
                >
                  <DownvoteIcon className="text-current" size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>{feedback === "negative" ? "Remove dislike" : "Dislike"}</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label="Delete meeting"
                  className="size-9 rounded-full"
                  disabled={isDeletingMeeting}
                  onClick={() => setIsDeleteDialogOpen(true)}
                  size="icon"
                  type="button"
                  variant="outline"
                >
                  <DeleteIcon className="text-destructive" size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Delete meeting</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label={minutesPanelOpen ? "Hide notes panel" : "Show notes panel"}
                  aria-pressed={minutesPanelOpen}
                  className="size-9 rounded-full"
                  onClick={() => setMinutesPanelOpen((o) => !o)}
                  size="icon"
                  type="button"
                  variant="outline"
                >
                  {minutesPanelOpen ? <PanelRightClose size={16} /> : <PanelRight size={16} />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{minutesPanelOpen ? "Hide notes" : "Show notes"}</p>
              </TooltipContent>
            </Tooltip>

          </div>
        ) : undefined}
      />

      <div ref={containerRef} className="flex flex-1 min-h-0 overflow-hidden">
        {/* ── Left: meeting details form ────────────────────────────────────── */}
        <div ref={scrollContainerRef} className="flex-1 min-w-0 overflow-auto p-6 pb-36 sm:pb-28">
          <div className="mx-auto flex w-full max-w-xl flex-col gap-6">
            {!activeId ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">No meeting selected</CardTitle>
                  <CardDescription>
                    Choose a meeting from the Meetings search dialog to view notes from the database.
                  </CardDescription>
                </CardHeader>
              </Card>
            ) : null}

            {loading ? (
              <DetailSkeleton />
            ) : null}

            {error ? (
              <Alert variant="destructive">
                <AlertCircleIcon className="size-4" />
                <AlertTitle>Unable to load meeting</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            {isRecordingInterrupted ? (
              <Alert>
                <AlertCircleIcon className="size-4" />
                <AlertTitle>Recording interrupted</AlertTitle>
                <AlertDescription>
                  This tab is no longer capturing audio. {hasPersistedTranscript
                    ? "Resume recording to keep adding to this meeting, or draft notes from the saved transcript."
                    : "Resume recording to continue this meeting. The microphone may ask for permission again."}
                </AlertDescription>
              </Alert>
            ) : null}

            {meeting ? (
              <div ref={detailsCardRef} className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Meeting details</h2>
                </div>
                <Field>
                    <div className="flex items-center gap-1.5">
                      <FieldLabel
                        className="text-xs font-normal uppercase tracking-wide text-muted-foreground"
                        htmlFor="meeting-context"
                      >
                        Meeting context
                      </FieldLabel>
                      <SaveIndicator status={leftColumnAutosaveStatus} />
                    </div>
                    <FieldContent>
                      <Textarea
                        className="min-h-28 resize-y"
                        disabled={isMeetingDetailsEditingDisabled}
                        id="meeting-context"
                        onChange={(event) => setMeetingContextDraft(event.target.value)}
                        placeholder="Add meeting context, goals, and agenda"
                        value={meetingContextDraft}
                      />
                    </FieldContent>
                </Field>

                <Field>
                    <div className="flex items-center gap-1.5">
                      <FieldLabel
                        className="text-xs font-normal uppercase tracking-wide text-muted-foreground"
                        htmlFor="meeting-instructions"
                      >
                        Instructions
                      </FieldLabel>
                      <SaveIndicator status={leftColumnAutosaveStatus} />
                    </div>
                    <FieldContent>
                      <Textarea
                        className="min-h-28 resize-y"
                        disabled={isMeetingDetailsEditingDisabled}
                        id="meeting-instructions"
                        onChange={(event) => setMeetingInstructionsDraft(event.target.value)}
                        placeholder="Tell the note taker how to write these minutes — tone, level of detail, what to emphasise or leave out"
                        value={meetingInstructionsDraft}
                      />
                      <FieldDescription>
                        Guides how the notes are written. Meeting context above describes what the
                        meeting is about.
                      </FieldDescription>
                    </FieldContent>
                </Field>

                <Field>
                  <div className="flex items-center gap-1.5">
                    <FieldLabel className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
                      Layout
                    </FieldLabel>
                    <SaveIndicator status={leftColumnAutosaveStatus} />
                  </div>
                  <FieldContent>
                    <div className="flex items-center gap-2">
                      <Select
                        disabled={isMeetingDetailsEditingDisabled || (isLoadingLayouts && availableLayouts.length === 0)}
                        onValueChange={setLayoutIdDraft}
                        value={resolvedLayoutIdDraft ?? ""}
                      >
                        <SelectTrigger className="w-full sm:w-80">
                          <SelectValue
                            placeholder={isLoadingLayouts ? "Loading layouts..." : DEFAULT_MEETING_LAYOUT_NAME}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {availableLayouts.map((layout) => (
                            <SelectItem key={layout.id} value={layout.id}>
                              {layout.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="shrink-0 text-muted-foreground"
                        disabled={isMeetingDetailsEditingDisabled}
                        onClick={() => {
                          setPreviewLayoutId(resolvedLayoutIdDraft);
                          setLayoutPreviewOpen(true);
                        }}
                      >
                        Preview
                      </Button>
                    </div>
                  </FieldContent>
                </Field>

                <Field className="gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <FieldLabel className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
                      Previous meeting
                    </FieldLabel>
                    <div className="flex items-center gap-1">
                      {previousMeetingIdDraft && (
                        <Button
                          onClick={handleClearPreviousMeeting}
                          size="sm"
                          type="button"
                          variant="ghost"
                        >
                          Clear
                        </Button>
                      )}
                      {previousMeetingNotesDraft && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              aria-label="View uploaded meeting notes"
                              className="size-6"
                              size="icon"
                              type="button"
                              variant="ghost"
                            >
                              <InfoIcon size={14} />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="max-h-72 w-80 overflow-auto">
                            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              Uploaded notes
                            </p>
                            <MessageResponse className="text-xs">
                              {previousMeetingNotesDraft}
                            </MessageResponse>
                          </PopoverContent>
                        </Popover>
                      )}
                      {previousMeetingNotesDraft && (
                        <Button
                          aria-label="Clear uploaded notes"
                          className="size-6"
                          disabled={isMeetingDetailsEditingDisabled}
                          onClick={handleClearPreviousNotes}
                          size="icon"
                          type="button"
                          variant="ghost"
                        >
                          <AnimatedXIcon size={14} />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      className="min-w-0 flex-1 justify-start gap-2 overflow-hidden bg-input/50 border-transparent hover:bg-input/60"
                      disabled={isMeetingDetailsEditingDisabled}
                      onClick={handleOpenImportDialog}
                      type="button"
                      variant="outline"
                    >
                      <SearchIcon className="shrink-0" size={14} />
                      {associatedPreviousMeetingTitle ? (
                        animateAssociatedPreviousMeetingTitle ? (
                          <TypingAnimation
                            as="span"
                            blinkCursor={false}
                            className="block truncate"
                            pauseDelay={0}
                            showCursor={false}
                            startOnView={false}
                            typeSpeed={20}
                            words={[associatedPreviousMeetingTitle]}
                          />
                        ) : (
                          <span className="block truncate">{associatedPreviousMeetingTitle}</span>
                        )
                      ) : (
                        "Select previous meeting"
                      )}
                    </Button>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            aria-label="Copy attendees from selected meeting"
                            className="size-9 shrink-0"
                            disabled={isMeetingDetailsEditingDisabled || !previousMeetingIdDraft}
                            onClick={() => void handleCopyPreviousMeetingAttendees()}
                            size="icon"
                            type="button"
                            variant="outline"
                          >
                            <UsersIcon size={14} />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p>
                          {previousMeetingIdDraft
                            ? "Copy attendees from the selected meeting"
                            : "Select a previous meeting first"}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                    <Button
                      className="shrink-0 gap-1.5"
                      disabled={isMeetingDetailsEditingDisabled || previousNotesUploadStatus === "uploading"}
                      onClick={() => previousNotesFileInputRef.current?.click()}
                      type="button"
                      variant="outline"
                    >
                      {previousNotesUploadStatus === "uploading" ? (
                        <Spinner className="size-3 shrink-0" />
                      ) : previousNotesUploadStatus === "done" ? (
                        <CheckCircle2Icon className="shrink-0 text-green-600" size={13} />
                      ) : (
                        <FileUp size={13} className="shrink-0" />
                      )}
                      Upload
                    </Button>
                  </div>
                  {(previousNotesUploadStatus === "uploading" || previousNotesFileName) && (
                    <p className="truncate text-xs text-muted-foreground">
                      {previousNotesUploadStatus === "uploading" ? "Uploading…" : previousNotesFileName}
                    </p>
                  )}
                  <input
                    ref={previousNotesFileInputRef}
                    accept={PREVIOUS_NOTES_SUPPORTED_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void handlePreviousNotesUpload(file);
                      e.target.value = "";
                    }}
                    type="file"
                  />
                </Field>

                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Attendees</p>
                    <SaveIndicator status={leftColumnAutosaveStatus} />
                  </div>

                  <div className="bg-background py-1">
                    {attendeesDraft.length > 0 ? (
                      <div className="mb-2 flex flex-wrap gap-2">
                        {attendeesDraft.map((attendee, attendeeIndex) => (
                          <Badge
                            className="flex items-center gap-1.5"
                            key={`${attendee.name}-${attendee.user_id ?? "external"}-${attendeeIndex}`}
                            variant="secondary"
                          >
                            <span>{attendee.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {attendee.user_id ? "internal" : "external"}
                            </span>
                            <button
                              aria-label={`Remove attendee ${attendee.name}`}
                              className="inline-flex items-center rounded-sm text-muted-foreground transition-colors hover:text-foreground"
                              disabled={isMeetingDetailsEditingDisabled}
                              onClick={() => removeAttendee(attendeeIndex)}
                              type="button"
                            >
                              <AnimatedXIcon size={12} />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    ) : null}

                    <Field className="gap-1">
                      <FieldLabel className="sr-only" htmlFor="attendees-input">
                        Attendees
                      </FieldLabel>
                      <Input
                        disabled={isMeetingDetailsEditingDisabled}
                        id="attendees-input"
                        onChange={(event) => setAttendeeInput(event.target.value)}
                        onKeyDown={handleAttendeeInputKeyDown}
                        placeholder="Type @ to search users, or add external attendee and press Enter"
                        value={attendeeInput}
                      />
                    </Field>

                    {findMentionQuery(attendeeInput) !== null ? (
                      <div className="mt-2 rounded-md border border-border bg-background">
                        {isSearchingUsers ? (
                          <p className="px-3 py-2 text-xs text-muted-foreground">Searching users...</p>
                        ) : userSearchResults.length > 0 ? (
                          <ul className="max-h-44 overflow-y-auto py-1">
                            {userSearchResults.map((user) => (
                              <li key={user.user_id}>
                                <button
                                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted"
                                  disabled={isMeetingDetailsEditingDisabled}
                                  onClick={() => addInternalAttendee(user)}
                                  type="button"
                                >
                                  <span className="truncate">{toUserSearchLabel(user)}</span>
                                  {user.email ? (
                                    <span className="truncate text-xs text-muted-foreground">{user.email}</span>
                                  ) : null}
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="px-3 py-2 text-xs text-muted-foreground">No users found.</p>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>

                {hasTranscriptActivity && !isGeneratingMinutes && !isProcessingNotes && !hasGeneratedMinutes ? (
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Live transcript</p>
                    <div className="py-1">
                      {isRecordingPaused ? (
                        <Shimmer as="p" className="text-sm text-muted-foreground" duration={1.4}>MIC MUTED</Shimmer>
                      ) : lastTranscriptLine ? (
                        <TextAnimate
                          animation="fadeIn"
                          as="p"
                          by="line"
                          className="whitespace-pre-wrap text-xs leading-6 text-muted-foreground"
                          key={lastTranscriptLine}
                        >
                          {lastTranscriptLine}
                        </TextAnimate>
                      ) : (
                        <Shimmer as="p" className="text-sm text-muted-foreground" duration={1.4}>
                          Waiting for transcript…
                        </Shimmer>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* dialogs stay in the left scroll column */}
          </div>
        </div>

        {/* ── Resize handle ───────────────────────────────────────────────────── */}
        {meeting && minutesPanelOpen ? (
          <div
            className="group relative z-10 w-px shrink-0 cursor-col-resize bg-border hover:bg-primary/60 transition-colors select-none"
            title="Drag to resize"
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              e.preventDefault();

              const startX = e.clientX;
              const startWidth = minutesPanelWidth;
              if (startWidth === null) return;

              const onMove = (moveEvent: MouseEvent) => {
                const delta = moveEvent.clientX - startX;
                setMinutesPanelWidth(Math.max(320, Math.min(800, startWidth - delta)));
              };
              const onUp = () => {
                window.removeEventListener("mousemove", onMove);
                window.removeEventListener("mouseup", onUp);
              };
              window.addEventListener("mousemove", onMove);
              window.addEventListener("mouseup", onUp);
            }}
          >
            <div className="absolute inset-y-0 -left-2 -right-2" />
          </div>
        ) : null}

        {/* ── Right: minutes side panel ─────────────────────────────────────── */}
        {meeting && minutesPanelOpen ? (
          <Artifact
            className="relative shrink-0 rounded-none border-0 shadow-none"
            style={{ width: minutesPanelWidth ? `${minutesPanelWidth}px` : undefined }}
          >
            {hasMinutesContent ? (
              <MarkdownPreviewToolbar>
                  <MarkdownPreviewToggle
                    mode={minutesView}
                    onModeChange={setMinutesView}
                    hideSplit
                    className="h-8"
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        aria-label="Undo last edit"
                        className="size-8 p-0 text-muted-foreground hover:text-foreground"
                        disabled={!selectionEditUndo}
                        onClick={() => void handleUndoSelectionEdit()}
                        size="icon"
                        type="button"
                        variant="ghost"
                      >
                        <Undo2 size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>{selectionEditUndo ? "Undo last edit" : "No edit to undo"}</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        aria-label="Redo last undo"
                        className="size-8 p-0 text-muted-foreground hover:text-foreground"
                        disabled={!selectionEditRedo}
                        onClick={() => void handleRedoSelectionEdit()}
                        size="icon"
                        type="button"
                        variant="ghost"
                      >
                        <Redo2 size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>{selectionEditRedo ? "Redo last undo" : "Nothing to redo"}</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        aria-label="Copy"
                        className="size-8 p-0 text-muted-foreground hover:text-foreground"
                        onClick={handleCopy}
                        size="icon"
                        type="button"
                        variant="ghost"
                      >
                        <CopyIcon size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>Copy</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        aria-label="Download DOCX"
                        className="size-8 p-0 text-muted-foreground hover:text-foreground"
                        disabled={!notesDocxPath}
                        onClick={() => void handleDownload()}
                        size="icon"
                        type="button"
                        variant="ghost"
                      >
                        {isProcessingDocx && !notesDocxPath ? (
                          <Spinner size={16} />
                        ) : (
                          <AnimatedDownloadIcon size={16} />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>
                        {notesDocxPath
                          ? "Download DOCX"
                          : isProcessingDocx
                            ? "Generating DOCX…"
                            : hasGeneratedMinutes
                              ? "DOCX is generated when you click Generate Docx and Send"
                              : "No minutes yet"}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="size-8 p-0 text-muted-foreground hover:text-foreground"
                        onClick={() => setMinutesEditorOpen(true)}
                      >
                        <Maximize2 size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">Open in full editor</TooltipContent>
                  </Tooltip>
              </MarkdownPreviewToolbar>
            ) : null}
            <ArtifactContent className="overscroll-contain scrollbar-minimal pb-36">

            {showChunkFailureBanner ? (
              <div className="mb-4 flex flex-col gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 shrink-0">⚠</span>
                  <div className="space-y-0.5">
                    <p className="font-medium">Transcription is failing</p>
                    <p className="text-destructive/80">Audio chunks have been failing for over 2 minutes. If this persists, stop the meeting to process any captured transcript, then start a new meeting.</p>
                  </div>
                </div>
                <div className="flex gap-2 pl-6">
                  <button
                    className="rounded border border-destructive/40 bg-background px-3 py-1 text-xs font-medium text-destructive hover:bg-destructive/5"
                    onClick={() => void handleStopAndNewMeeting()}
                    type="button"
                  >
                    Stop &amp; Create New Meeting
                  </button>
                  <button
                    className="rounded border border-destructive/20 bg-background px-3 py-1 text-xs font-medium text-destructive hover:bg-destructive/5"
                    onClick={() => setShowChunkFailureBanner(false)}
                    type="button"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ) : null}

            {isGeneratingMinutes ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <Shimmer as="p" className="text-base font-medium" duration={1.4}>
                  Queueing generation...
                </Shimmer>
              </div>
            ) : isProcessingNotes ? (
              <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
                <Shimmer as="p" className="text-base font-medium" duration={1.4}>
                  Generating minutes in the background...
                </Shimmer>
                <p className="text-sm text-muted-foreground">The draft will appear here automatically when it&apos;s ready.</p>
              </div>
            ) : isGenerationFailed ? (
              <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
                <p className="text-sm font-medium text-destructive">Minutes generation failed.</p>
                <Button size="sm" variant="outline" onClick={() => void handleRetry()}>Retry generation</Button>
              </div>
            ) : hasMinutesContent ? (
              <MarkdownPreviewContent
                mode={minutesView}
                onChange={handleMinutesDraftChange}
                onSelectionEdit={handleSelectionEdit}
                previewClassName={MARKDOWN_DOCUMENT_PREVIEW_CLASS_NAME}
                textareaClassName="mx-auto mt-16 mb-6 h-auto min-h-72 w-full max-w-3xl field-sizing-content overflow-hidden"
                textareaPlaceholder="Meeting notes…"
                value={minutesDraft}
                emptyPreviewText="No minutes available yet."
              />
            ) : !hasTranscriptActivity ? (
              <div className="flex items-center justify-center py-24">
                <TextAnimate
                  animation="fadeIn"
                  as="p"
                  by="line"
                  className="max-w-xs whitespace-pre-line text-center text-sm leading-7 text-muted-foreground"
                >
                  {"Start recording.\n\nAdd meeting details if you want.\n\nPress Stop & Draft to create meeting notes."}
                </TextAnimate>
              </div>
            ) : isRecordingInterrupted ? (
              <div className="flex items-center justify-center py-24 text-center">
                <p className="max-w-xs text-sm leading-7 text-muted-foreground">
                  {hasPersistedTranscript
                    ? "Recording interrupted. Resume to keep capturing audio, or draft from the saved transcript."
                    : "Recording interrupted. Resume to keep capturing audio for this meeting."}
                </p>
              </div>
            ) : isRecordingCompleted ? (
              <div className="flex items-center justify-center py-24">
                <TextAnimate
                  animation="fadeIn"
                  as="p"
                  by="line"
                  className="max-w-xs whitespace-pre-line text-center text-sm leading-7 text-muted-foreground"
                >
                  {"Recording stopped.\n\nAdd any extra context above,\nthen click Draft."}
                </TextAnimate>
              </div>
            ) : (
              <div className="flex items-center justify-center py-24">
                <div className="max-w-2xl space-y-3 text-center">
                  {isRecordingPaused ? (
                    <Shimmer as="p" className="text-sm text-muted-foreground" duration={1.4}>MIC MUTED</Shimmer>
                  ) : (
                    <Shimmer as="p" className="text-sm text-muted-foreground" duration={1.4}>Recording…</Shimmer>
                  )}
                  <p className="text-xs text-muted-foreground">See the live transcript under Attendees.</p>
                </div>
              </div>
            )}
            </ArtifactContent>
          </Artifact>
        ) : null}
      </div>

      {/* ── Minutes full editor ──────────────────────────────────────────── */}
      <Dialog open={minutesEditorOpen} onOpenChange={setMinutesEditorOpen}>
            <DialogContent className="sm:max-w-6xl">
              <DialogHeader>
                <DialogTitle>Meeting Notes</DialogTitle>
                <DialogDescription>
                  Review and edit the generated notes for this meeting.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-1">
                <div className="flex justify-end">
                  <MarkdownPreviewToggle mode={minutesEditorMode} onModeChange={setMinutesEditorMode} />
                </div>
                <MarkdownPreviewContent
                  mode={minutesEditorMode}
                  value={minutesDraft}
                  onChange={handleMinutesDraftChange}
                  onSelectionEdit={handleSelectionEdit}
                  textareaPlaceholder="Meeting notes…"
                  textareaClassName="min-h-[60vh]"
                  previewClassName="minutes-prose min-h-[60vh] px-6 py-5"
                />
              </div>
              <DialogFooter>
                <Button onClick={() => setMinutesEditorOpen(false)}>Done</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <SearchDialog
            open={isImportDialogOpen}
            onOpenChange={setIsImportDialogOpen}
            title="Associate previous meeting"
            description="Choose the meeting to link as previous context"
            placeholder="Search meetings..."
            emptyMessage="No meetings available to associate."
            groups={previousMeetingsGroups}
            onSelect={(id) => {
              handleSelectPreviousMeeting(id);
            }}
          />

      {meeting ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center px-4">
          <div className="pointer-events-auto flex flex-col items-center gap-1.5 rounded-2xl border border-border bg-card/95 p-2 shadow-lg backdrop-blur sm:flex-row sm:rounded-full">
            <div className="flex w-full items-center justify-center gap-1 sm:inline-flex sm:w-auto">
              {/* ── Lifecycle: Play → Stop & Draft → Retry + Send ─────────── */}
              {isPlaybackRunning ? (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          aria-label="Stop & Draft"
                          className="h-9 rounded-full border-destructive/40 bg-destructive/10 px-3 text-sm text-destructive hover:bg-destructive/15"
                          disabled={isStoppingRecording || isGeneratingMinutes}
                          onClick={() => void handleStopAndDraft()}
                          size="sm"
                          type="button"
                          variant="outline"
                        >
                          {isStoppingRecording || isGeneratingMinutes ? (
                            <Spinner className="size-4" />
                          ) : (
                            <Square className="text-current" size={16} />
                          )}
                          <span className="hidden sm:inline">Stop &amp; Draft</span>
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>
                        {isStoppingRecording || isGeneratingMinutes
                          ? "Stopping recording and starting the draft..."
                          : "Stop the recording and draft the minutes"}
                      </p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        aria-label={isRecordingPaused ? "Unmute" : "Mute"}
                        className="h-9 rounded-full border-border bg-muted px-3 text-sm text-muted-foreground hover:bg-muted/90"
                        onClick={handleToggleMute}
                        size="sm"
                        type="button"
                        variant="outline"
                      >
                        {isRecordingPaused ? (
                          <MicOffIcon className="text-current" size={16} />
                        ) : (
                          <MicIcon className="text-current" size={16} />
                        )}
                        <span className="hidden sm:inline">{isRecordingPaused ? "Unmute" : "Mute"}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>{isRecordingPaused ? "Unmute" : "Mute"}</p>
                    </TooltipContent>
                  </Tooltip>
                </>
              ) : hasGeneratedMinutes ? (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          aria-label="Regenerate draft"
                          className="h-9 rounded-full px-3 text-sm"
                          disabled={
                            isGeneratingMinutes ||
                            isProcessingInBackground ||
                            isSendingEmail ||
                            !hasPersistedTranscript
                          }
                          onClick={() => void handleRedraft()}
                          size="sm"
                          type="button"
                          variant="outline"
                        >
                          {isGeneratingMinutes || isProcessingNotes ? (
                            <Spinner className="size-3.5" />
                          ) : (
                            <RefreshCWIcon className="text-current" size={14} />
                          )}
                          <span className="hidden sm:inline">
                            {isGeneratingMinutes || isProcessingNotes ? "Drafting…" : "Regenerate"}
                          </span>
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>
                        {isGeneratingMinutes || isProcessingNotes
                          ? "Draft in progress…"
                          : !hasPersistedTranscript
                            ? "Transcript no longer available — it was deleted when the minutes were sent"
                            : "Re-draft the minutes from the retained transcript"}
                      </p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            aria-label={meeting.email_sent ? "Generate Docx and Send again" : "Generate Docx and Send"}
                            className={cn(
                              "h-9 rounded-full px-3 text-sm",
                              meeting.email_sent && "bg-muted text-activated"
                            )}
                            disabled={isSendingEmail || isGeneratingMinutes || isProcessingInBackground}
                            onClick={() => setIsSendDialogOpen(true)}
                            size="sm"
                            type="button"
                            variant="outline"
                          >
                            {isSendingEmail ? (
                              <>
                                <Spinner className="size-3.5" />
                                <span className="hidden sm:inline">
                                  {meeting.status === "PROCESSING_DOCX" ? "Generating DOCX…" : "Sending…"}
                                </span>
                              </>
                            ) : (
                              <>
                                <SendIcon
                                  className={meeting.email_sent ? "text-activated" : "text-current"}
                                  size={14}
                                />
                                <span className="hidden sm:inline">
                                  {meeting.email_sent ? "Sent" : "Generate Docx and Send"}
                                </span>
                              </>
                            )}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p>
                          {isSendingEmail
                            ? meeting.status === "PROCESSING_DOCX"
                              ? "Generating the DOCX…"
                              : "Sending the minutes email…"
                            : meeting.email_sent
                              ? "Regenerate the DOCX and send meeting minutes again"
                              : "Generate the DOCX and email the meeting minutes — the transcript is deleted after a successful send"}
                        </p>
                      </TooltipContent>
                  </Tooltip>
                </>
              ) : isGeneratingMinutes || isProcessingNotes || isStoppingRecording ? (
                <div className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-muted px-3 text-sm text-muted-foreground">
                  <Spinner className="size-3.5" />
                  {isStoppingRecording ? "Stopping…" : "Drafting…"}
                </div>
              ) : isRecordingInterrupted ? (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          aria-label="Resume recording"
                          className="h-9 rounded-full border-chart-1/60 bg-chart-1/30 px-3 text-sm text-foreground hover:bg-chart-1/40"
                          disabled={isStartDisabled}
                          onClick={() => setIsConsentDialogOpen(true)}
                          size="sm"
                          type="button"
                          variant="outline"
                        >
                          <PlayIcon className="text-current" size={16} />
                          <span className="hidden sm:inline">Resume recording</span>
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>{anotherRecordingActive ? "Finish the other active recording first" : "Resume capturing audio for this meeting"}</p>
                    </TooltipContent>
                  </Tooltip>
                  {canGenerateNotes ? (
                    <Button
                      aria-label="Draft saved transcript"
                      className="h-9 rounded-full px-3 text-sm"
                      onClick={() => void startDraftGeneration()}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      <PencilLine className="text-current" size={16} />
                      <span className="hidden sm:inline">Draft saved transcript</span>
                    </Button>
                  ) : null}
                </>
              ) : canGenerateNotes ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="relative inline-block overflow-hidden rounded-full">
                      <BorderBeam
                        size={36}
                        duration={2.5}
                        borderWidth={2}
                        colorFrom="var(--primary)"
                        colorTo="color-mix(in srgb, var(--primary) 20%, transparent)"
                      />
                      <Button
                        aria-label="Draft minutes"
                        className="h-9 rounded-full border-primary/40 bg-primary/10 px-3 text-sm text-primary hover:bg-primary/15"
                        onClick={() => void startDraftGeneration()}
                        size="sm"
                        type="button"
                        variant="outline"
                      >
                        <PencilLine className="text-current" size={16} />
                        <span className="hidden sm:inline">Draft</span>
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>Generate the minutes draft from the transcript</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        aria-label="Start recording"
                        className="h-9 rounded-full border-activated/40 bg-activated/10 px-3 text-sm text-activated hover:bg-activated/15"
                        disabled={isStartDisabled}
                        onClick={() => setIsConsentDialogOpen(true)}
                        size="sm"
                        type="button"
                        variant="outline"
                      >
                        <PlayIcon className="text-current" size={16} />
                        <span className="hidden sm:inline">Start Recording</span>
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>
                      {anotherRecordingActive
                        ? "Finish the other active recording first"
                        : isRecordingCompleted
                        ? "Recording stopped"
                        : isStartDisabled
                          ? "Disabled: recording is locked"
                          : "Start recording"}
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}

              {liveAnalyser ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <WaveformBars analyser={liveAnalyser} barClassName={isRecordingPaused ? "bg-muted-foreground/40" : "bg-activated"} />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top"><p>{isRecordingPaused ? "Microphone (muted)" : "Microphone"}</p></TooltipContent>
                </Tooltip>
              ) : null}
              {liveCallAnalyser ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <WaveformBars analyser={liveCallAnalyser} barClassName="bg-[#6264A7]" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top"><p>Shared Audio</p></TooltipContent>
                </Tooltip>
              ) : null}

            </div>
          </div>
        </div>
      ) : null}

      <Dialog open={isConsentDialogOpen} onOpenChange={setIsConsentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>AI Recording Consent</DialogTitle>
            <DialogDescription>
              Do all meeting participants consent to AI being used to generate notes for this meeting? The audio recording is not retained, and the transcript is deleted once the meeting minutes email has been sent.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
            <Switch
              checked={includeCallAudio}
              id="include-call-audio"
              onCheckedChange={setIncludeCallAudio}
            />
            <div className="flex flex-col gap-0.5">
              <label className="cursor-pointer text-sm font-medium leading-none" htmlFor="include-call-audio">
                On a Teams or video call?
              </label>
              <p className="text-xs text-muted-foreground">
                Turn this on — especially if you&apos;re wearing headphones. Without it, only your voice is recorded and remote participants will be missed. You&apos;ll be asked to share your screen; select your Teams window and make sure &quot;Share audio&quot; is checked.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setIsConsentDialogOpen(false)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setIsConsentDialogOpen(false);
                void handleStartRecording();
              }}
              type="button"
            >
              {isRecordingInterrupted ? "Consent Given — Resume Recording" : "Consent Given — Start Recording"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isNoChangesDialogOpen} onOpenChange={setIsNoChangesDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nothing to re-draft</DialogTitle>
            <DialogDescription>
              Nothing has changed since the last draft. Generation will not run.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setIsNoChangesDialogOpen(false)}
              type="button"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isSendDialogOpen} onOpenChange={setIsSendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Docx and Send</DialogTitle>
            <DialogDescription>
              Woods AI will generate the DOCX from the current draft, email the notes with it attached, and delete the transcript after a successful send.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setIsSendDialogOpen(false)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setIsSendDialogOpen(false);
                void handleSendEmail();
              }}
              type="button"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete meeting</DialogTitle>
            <DialogDescription>
              {meeting?.title?.trim()
                ? `\"${meeting.title}\" will be permanently deleted. This cannot be undone.`
                : "This meeting will be permanently deleted. This cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              disabled={isDeletingMeeting}
              onClick={() => setIsDeleteDialogOpen(false)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={isDeletingMeeting}
              onClick={() => void handleDeleteMeeting()}
              type="button"
              variant="destructive"
            >
              {isDeletingMeeting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={layoutPreviewOpen}
        onOpenChange={(open) => {
          setLayoutPreviewOpen(open);
          if (!open) setPreviewLayoutId(null);
        }}
      >
        <DialogContent className="flex flex-col gap-0 p-0 h-dvh w-full sm:max-w-2xl">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle>Layout preview</DialogTitle>
            <DialogDescription>
              Choose a layout to see how your meeting minutes will be structured.
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-4 shrink-0">
            <Select
              value={previewLayoutId ?? resolvedLayoutIdDraft ?? ""}
              onValueChange={setPreviewLayoutId}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a layout..." />
              </SelectTrigger>
              <SelectContent>
                {availableLayouts.map((layout) => (
                  <SelectItem key={layout.id} value={layout.id}>
                    {layout.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {previewingLayout?.description?.trim() && (
              <p className="mt-2 text-xs text-muted-foreground">
                {previewingLayout.description}
              </p>
            )}
          </div>
          <div className="flex-1 flex flex-col px-6 py-4 min-h-0 border-t border-border">
            {previewingLayout?.structure ? (
              <MarkdownPreviewContent
                mode="preview"
                value={previewingLayout.structure}
                readOnly
                previewClassName="minutes-prose h-full min-h-0"
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                No preview available for this layout.
              </p>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border shrink-0 flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setLayoutPreviewOpen(false);
                setPreviewLayoutId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (previewLayoutId) {
                  setLayoutIdDraft(previewLayoutId);
                }
                setLayoutPreviewOpen(false);
                setPreviewLayoutId(null);
              }}
            >
              Choose this layout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
